// Native WebSocket implementation (no Socket.IO dependency)
if (window.MIPTECH_DEBUG) console.log('%c MIPTech Widget Script Loaded ', 'background: #222; color: #bada55; font-size: 20px');
try { window.MIPTechWidgetLoaded = true; } catch (e) { }

// Widget states
const STATES = {
  INITIALIZING: 'initializing',
  READY: 'ready',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  ERROR: 'error',
  PREVIEW: 'preview'  // Admin preview mode - no WebSocket connection
};

// Events
const EVENTS = {
  WIDGET_READY: 'widget:ready',
  WIDGET_OPENED: 'widget:opened',
  WIDGET_CLOSED: 'widget:closed',
  MESSAGE_RECEIVED: 'message:received',
  MESSAGE_SENT: 'message:sent',
  CONNECTION_STATUS: 'connection:status'
};

const CHAT_HISTORY_SCHEMA_VERSION = 'no-local-history-v1';

const AUTHORITATIVE_CAPABILITY_EVENTS = new Set([
  'connection_established',
  'connection_ready',
  'chat_created',
  'chat_joined',
  'chat_ready',
  'joined'
]);

export default class MIPTechWidget {
  constructor(config = {}) {
    this.log('MIPTechWidget Constructor called with config:', config);
    this.version = '2.5.0-enterprise';
    this.instanceId = this.generateInstanceId();

    // Configuration with enterprise defaults
    this.config = {
      tenantId: config.tenantId || config.tenant_id || '',
      apiUrl: config.apiUrl || 'http://localhost:8080',
      wsUrl: config.wsUrl || 'ws://localhost:8080',
      position: config.position || 'bottom-right',
      autoInit: config.autoInit !== false,
      brandColor: config.brandColor || '#007cba',
      accentColor: config.accentColor || '#0073aa',

      // Customization options
      botName: config.botName || 'Chat Support',
      botAvatarUrl: config.botAvatarUrl || '',
      botAvatarUrl: config.botAvatarUrl || '',
      userAvatarUrl: config.userAvatarUrl || '',
      showUserAvatar: !!config.userAvatarUrl,
      headerLogoUrl: config.headerLogoUrl || config.header_logo_url || '', // Support both cases

      // Enterprise features
      features: {
        fileUpload: config.features?.fileUpload || false,
        voiceMessage: config.features?.voiceMessage || false,
        videoCall: config.features?.videoCall || false,
        screenShare: config.features?.screenShare || false,
        chatHistory: config.features?.chatHistory === true,
        typingIndicator: config.features?.typingIndicator !== false,
        readReceipts: config.features?.readReceipts || false,
        feedbackActions: config.features?.feedbackActions || false,
        copyAction: config.features?.copyAction || false,
        copyTracking: config.features?.copyTracking || false,
        ...config.features
      },

      // Advanced options
      maxFileSize: config.maxFileSize || 10 * 1024 * 1024, // 10MB
      allowedFileTypes: config.allowedFileTypes || ['image/*', '.pdf', '.doc', '.docx'],
      reconnectAttempts: config.reconnectAttempts || 5,
      heartbeatInterval: config.heartbeatInterval || 30000,

      // Preview mode - disables WebSocket and input for admin preview
      previewMode: config.previewMode || false,

      // Debug mode - controls console.log visibility
      debugMode: config.debugMode || false,

      // Add-to-cart behavior: 'auto' | 'redirect' | 'ajax'
      addToCartMode: config.addToCartMode || 'auto',

      ...config
    };

    this.state = STATES.INITIALIZING;
    this.isVisible = false;
    this.initialized = false;

    // GDPR in-widget consent state
    this.consentState = this._resolveConsentState();
    this.consentSubmitting = false;

    // Components
    this.container = null;
    this.toggleButton = null;
    this.chatWindow = null;
    this.messageContainer = null;
    this.inputField = null;

    // Real-time communication
    this.socket = null;
    this.session = null;
    this.reconnectCount = 0;
    this.typingTimeout = null;

    // Message handling
    this.messages = [];
    this.messageQueue = [];
    this.isTyping = false;
    this.serverCapabilities = {
      messageFeedback: false,
      messageCopy: false,
      messageCopyTracking: false
    };
    this.serverCapabilitiesAuthoritative = false;
    this.feedbackStates = new Map();
    this.pendingFeedbackEvents = new Map();
    this.feedbackAckTimeoutMs = 8000;

    // Streaming response handling
    this.streamingMessage = null;
    this.streamingMessageId = null;
    this.streamingContent = '';

    // Chat history persistence
    this.chatHistory = [];
    this._renderedMessageIds = new Set();
    this._chatRestored = false;
    this._restoring = false;
    this._storageDegraded = false;
    this._restoredChatId = null;
    this.currentLiveChatId = null;
    this.currentLiveChatToken = null;

    // Concurrency guards
    this._bootstrapping = false;
    this._reconnectingSocket = false;
    this._isPreGenTokenFlow = false;
    this._reconnectTimer = null;

    // Product recommendations pending state
    this.pendingProducts = null;
    this.pendingProductsMode = null;
    this.pendingHeroId = null;
    this.responseTimeout = null;

    if (this.config.autoInit) {
      this.init();
    }
  }

  /**
   * Debug logger - only outputs when debugMode is enabled
   */
  log(...args) {
    if (this.config && this.config.debugMode) {
      console.log(...args);
    }
  }

  isFeatureEnabled(featureName) {
    const value = this.config?.features?.[featureName];
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  extractCapabilitiesPayload(message) {
    return message?.data?.capabilities ||
      message?.capabilities ||
      message?.data?.server_capabilities ||
      message?.server_capabilities ||
      null;
  }

  normalizeServerCapabilities(rawCapabilities) {
    const readCapability = (keys) => {
      if (!rawCapabilities) return false;

      if (Array.isArray(rawCapabilities)) {
        return keys.some(key => rawCapabilities.includes(key));
      }

      if (typeof rawCapabilities === 'object') {
        return keys.some(key => {
          const value = rawCapabilities[key];
          return value === true || value === 'true' || value === 1 || value === '1';
        });
      }

      return false;
    };

    return {
      messageFeedback: readCapability(['message_feedback', 'feedback', 'feedback_actions']),
      messageCopy: readCapability(['message_copy', 'message_copy_tracking', 'copy_tracking', 'copy']),
      messageCopyTracking: readCapability(['message_copy_tracking', 'copy_tracking'])
    };
  }

  updateServerCapabilities(message, authoritative = false) {
    const rawCapabilities = this.extractCapabilitiesPayload(message);

    if (!rawCapabilities) {
      return;
    }

    if (!authoritative && this.serverCapabilitiesAuthoritative) {
      return;
    }

    this.serverCapabilities = this.normalizeServerCapabilities(rawCapabilities);
    if (authoritative) {
      this.serverCapabilitiesAuthoritative = true;
    }
    this.refreshMessageActions();
  }

  ingestServerCapabilities(message) {
    if (!this.extractCapabilitiesPayload(message)) return;
    this.updateServerCapabilities(
      message,
      AUTHORITATIVE_CAPABILITY_EVENTS.has(message?.type)
    );
  }

  hasAuthoritativeCapability(capabilityName) {
    return this.serverCapabilitiesAuthoritative && this.serverCapabilities?.[capabilityName] === true;
  }

  getAssistantMessageIdFromEvent(message) {
    const id = message?.data?.assistant_message_id ||
      message?.data?.message_id ||
      message?.assistant_message_id ||
      message?.message_id ||
      null;

    if (!id || this.isGeneratedMessageId(id)) return null;
    return String(id);
  }

  hasPersistedAssistantMessageId(message) {
    return this.isAssistantMessage(message) &&
      !!message?.id &&
      message.idSource === 'backend' &&
      !this.isGeneratedMessageId(message.id);
  }

  isGeneratedMessageId(messageId) {
    return /^msg_/i.test(String(messageId || ''));
  }

  extractChatIdFromEvent(message) {
    return message?.data?.chat_id || message?.chat_id || null;
  }

  beginLiveChatScope(chatId) {
    if (!chatId) return;

    const normalizedChatId = String(chatId);

    if (this.currentLiveChatId !== normalizedChatId) {
      this.currentLiveChatId = normalizedChatId;
      this.currentLiveChatToken = this.generateClientEventId('chat');
    }

    if (!this.currentLiveChatToken) {
      this.currentLiveChatToken = this.generateClientEventId('chat');
    }
  }

  attachLiveChatMetadata(message) {
    if (this.isAssistantMessage(message) && this.currentLiveChatId && this.currentLiveChatToken) {
      message.liveChatId = this.currentLiveChatId;
      message.liveChatToken = this.currentLiveChatToken;
    }
    return message;
  }

  belongsToCurrentLiveChat(message) {
    return !!message &&
      !!this.currentLiveChatId &&
      !!this.currentLiveChatToken &&
      message.liveChatId === this.currentLiveChatId &&
      message.liveChatToken === this.currentLiveChatToken;
  }

  normalizeLiveAssistantMessage(rawMessage, base = {}) {
    const assistantMessageId = this.getAssistantMessageIdFromEvent(rawMessage);

    return this.attachLiveChatMetadata({
      ...base,
      id: assistantMessageId || base.id || this.generateMessageId(),
      idSource: assistantMessageId ? 'backend' : (base.idSource || 'generated'),
      role: 'assistant',
      senderId: 'assistant',
      senderType: 'bot',
      type: base.type || 'text'
    });
  }

  isSocketOpen() {
    const openState = typeof WebSocket !== 'undefined' ? WebSocket.OPEN : 1;
    return this.socket && this.socket.readyState === openState;
  }

  generateClientEventId(prefix = 'evt') {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    const randomHex = (length) => Array.from({ length }, () => (
      Math.floor(Math.random() * 16).toString(16)
    )).join('');

    return [
      randomHex(8),
      randomHex(4),
      `4${randomHex(3)}`,
      `${(8 + Math.floor(Math.random() * 4)).toString(16)}${randomHex(3)}`,
      randomHex(12)
    ].join('-');
  }

  isAssistantMessage(message) {
    const senderType = message?.senderType || message?.sender?.type || message?.role;
    return senderType === 'bot' || senderType === 'assistant';
  }

  shouldShowMessageActions(message) {
    if (!message || !message.id || !this.isAssistantMessage(message)) return false;
    if (message.isStreaming || message.status === 'streaming') return false;
    return this.canShowCopyAction(message) || this.canShowFeedbackActions(message);
  }

  canShowCopyAction(message) {
    return this.isFeatureEnabled('copyAction') && !!this.getPlainTextForCopy(message);
  }

  canShowFeedbackActions(message) {
    return this.isFeatureEnabled('feedbackActions') &&
      this.hasAuthoritativeCapability('messageFeedback') &&
      this.hasPersistedAssistantMessageId(message) &&
      this.belongsToCurrentLiveChat(message);
  }

  canTrackCopyAction() {
    return this.isFeatureEnabled('copyTracking') &&
      this.hasAuthoritativeCapability('messageCopyTracking');
  }

  getPlainTextForCopy(message) {
    if (!message || typeof message.content === 'undefined' || message.content === null) {
      return '';
    }

    return String(message.content)
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\u00a0/g, ' ')
      .trim();
  }

  renderMessageActions(message, messageElement) {
    if (!messageElement) return;

    const existingActions = messageElement.querySelector('.miptech-message-actions');
    if (existingActions) {
      existingActions.remove();
    }

    if (!this.shouldShowMessageActions(message)) return;

    const contentElement = messageElement.querySelector('.miptech-message-content');
    if (!contentElement) return;
    const metaElement = this.getMessageMetaElement(contentElement);
    if (!metaElement) return;

    const actionsElement = document.createElement('div');
    actionsElement.className = 'miptech-message-actions';
    actionsElement.setAttribute('aria-label', 'Message actions');

    if (this.canShowCopyAction(message)) {
      actionsElement.appendChild(this.createMessageActionButton(
        'copy',
        'Copy message',
        () => this.copyMessage(message, messageElement)
      ));
    }

    if (this.canShowFeedbackActions(message)) {
      actionsElement.appendChild(this.createMessageActionButton(
        'feedback-positive',
        'Helpful response',
        () => this.sendMessageFeedback(message, 'positive', messageElement)
      ));
      actionsElement.appendChild(this.createMessageActionButton(
        'feedback-negative',
        'Not helpful response',
        () => this.sendMessageFeedback(message, 'negative', messageElement)
      ));
    }

    const statusElement = document.createElement('span');
    statusElement.className = 'miptech-message-action-status';
    statusElement.setAttribute('aria-live', 'polite');
    actionsElement.appendChild(statusElement);

    metaElement.appendChild(actionsElement);
    this.applyFeedbackStateToElement(message.id, messageElement);
  }

  getMessageMetaElement(contentElement) {
    let metaElement = contentElement.querySelector('.miptech-message-meta');
    if (metaElement) return metaElement;

    metaElement = document.createElement('div');
    metaElement.className = 'miptech-message-meta';

    const timeElement = Array.from(contentElement.children).find((child) =>
      child.classList && child.classList.contains('miptech-message-time')
    );

    if (timeElement) {
      metaElement.appendChild(timeElement);
    }

    contentElement.appendChild(metaElement);
    return metaElement;
  }

  createMessageActionButton(action, label, onClick) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `miptech-message-action miptech-message-action-${action}`;
    button.setAttribute('aria-label', label);
    button.setAttribute('title', label);
    button.innerHTML = this.getMessageActionIcon(action);
    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      onClick();
    });
    return button;
  }

  getMessageActionIcon(action) {
    const icons = {
      copy: '<svg viewBox="0 0 20 20" aria-hidden="true"><rect x="7" y="7" width="9" height="9" rx="2"></rect><rect x="4" y="4" width="9" height="9" rx="2"></rect></svg>',
      'feedback-positive': '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M6.5 8.2v7.1"></path><path d="M6.5 8.2H4.6a1.4 1.4 0 0 0-1.4 1.4v4.3a1.4 1.4 0 0 0 1.4 1.4h1.9"></path><path d="M6.5 8.2 10 4.1a1.25 1.25 0 0 1 2.2.9v2.4h3.1a1.5 1.5 0 0 1 1.46 1.83l-.95 4.25a2.25 2.25 0 0 1-2.2 1.82H6.5"></path></svg>',
      'feedback-negative': '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M13.5 11.8V4.7"></path><path d="M13.5 11.8h1.9a1.4 1.4 0 0 0 1.4-1.4V6.1a1.4 1.4 0 0 0-1.4-1.4h-1.9"></path><path d="M13.5 11.8 10 15.9a1.25 1.25 0 0 1-2.2-.9v-2.4H4.7a1.5 1.5 0 0 1-1.46-1.83l.95-4.25A2.25 2.25 0 0 1 6.4 4.7h7.1"></path></svg>'
    };

    return icons[action] || '';
  }

  async copyMessage(message, messageElement) {
    const text = this.getPlainTextForCopy(message);
    if (!text) return;

    try {
      await this.writeTextToClipboard(text);
      this.showMessageActionStatus(messageElement, 'Copied');
      this.emit('message:copied', { messageId: message.id });
      this.trackCopyAction(message);
    } catch (error) {
      this.showMessageActionStatus(messageElement, 'Copy failed');
      this.log('Copy failed', error);
    }
  }

  async writeTextToClipboard(text) {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.top = '-1000px';
    textarea.style.left = '-1000px';
    document.body.appendChild(textarea);
    textarea.select();

    try {
      const copied = document.execCommand && document.execCommand('copy');
      if (!copied) {
        throw new Error('Clipboard fallback failed');
      }
    } finally {
      textarea.remove();
    }
  }

  trackCopyAction(message) {
    if (!this.canTrackCopyAction()) return;

    this.sendPrivacySafeWidgetEvent('message_copy', message, {
      client_event_id: this.generateClientEventId('copy')
    });
  }

  sendMessageFeedback(message, feedback, messageElement) {
    if (!this.canShowFeedbackActions(message)) return;

    if (!this.isSocketOpen() || !this.session?.chatId) {
      this.showMessageActionStatus(messageElement, 'Feedback unavailable');
      return;
    }

    const messageId = String(message.id);
    const previousState = this.feedbackStates.get(messageId) || null;
    const clientEventId = this.generateClientEventId('feedback');

    this.feedbackStates.set(messageId, {
      value: feedback,
      pending: true,
      clientEventId
    });

    const timeoutId = setTimeout(() => {
      this.handleFeedbackEventFailed(clientEventId);
    }, this.feedbackAckTimeoutMs);

    this.pendingFeedbackEvents.set(clientEventId, {
      messageId,
      value: feedback,
      previousState,
      timeoutId
    });

    this.applyFeedbackStateToElement(messageId, messageElement);
    this.showMessageActionStatus(messageElement, 'Saving feedback...');

    const sent = this.sendPrivacySafeWidgetEvent('message_feedback', message, {
      feedback: this.toServerFeedbackValue(feedback),
      client_event_id: clientEventId
    });

    if (!sent) {
      this.rollbackFeedback(clientEventId);
      this.showMessageActionStatus(messageElement, 'Feedback not saved');
      return;
    }

    this.emit('message:feedback', { messageId, feedback });
  }

  toServerFeedbackValue(feedback) {
    if (feedback === 'positive') return 'like';
    if (feedback === 'negative') return 'dislike';
    return feedback;
  }

  sendPrivacySafeWidgetEvent(eventType, message, extraData = {}) {
    if (!this.isSocketOpen() || !this.session?.chatId || !message?.id) {
      return false;
    }

    const payload = {
      type: eventType,
      data: {
        chat_id: this.session.chatId,
        message_id: String(message.id),
        assistant_message_id: String(message.id),
        client_event_id: extraData.client_event_id || this.generateClientEventId('event'),
        event_ts: new Date().toISOString(),
        channel: 'wordpress'
      }
    };

    Object.keys(extraData).forEach((key) => {
      payload.data[key] = extraData[key];
    });

    try {
      this.socket.send(JSON.stringify(payload));
      return true;
    } catch (error) {
      this.log('Failed to send widget event', eventType, error);
      return false;
    }
  }

  handleFeedbackAck(message) {
    const data = message?.data || message || {};
    const clientEventId = data.client_event_id || message?.client_event_id;
    if (!clientEventId) return;

    const pending = this.pendingFeedbackEvents.get(clientEventId);
    if (!pending) return;

    const currentState = this.feedbackStates.get(pending.messageId);
    if (!currentState || currentState.clientEventId !== clientEventId || currentState.pending !== true) {
      this.clearFeedbackPendingTimer(pending);
      this.pendingFeedbackEvents.delete(clientEventId);
      return;
    }

    this.clearFeedbackPendingTimer(pending);
    this.feedbackStates.set(pending.messageId, {
      value: pending.value,
      pending: false,
      clientEventId
    });
    this.pendingFeedbackEvents.delete(clientEventId);
    this.refreshMessageActionById(pending.messageId);
    this.showMessageActionStatusById(pending.messageId, 'Feedback saved');
  }

  handleFeedbackRejected(message) {
    const data = message?.data || message || {};
    const clientEventId = data.client_event_id || message?.client_event_id;
    if (!clientEventId) return;

    const pending = this.pendingFeedbackEvents.get(clientEventId);
    if (!pending) return;

    this.rollbackFeedback(clientEventId);
    this.showMessageActionStatusById(pending.messageId, 'Feedback not saved');
  }

  handleFeedbackEventFailed(clientEventId) {
    const pending = this.pendingFeedbackEvents.get(clientEventId);
    if (!pending) return;

    this.rollbackFeedback(clientEventId);
    this.showMessageActionStatusById(pending.messageId, 'Feedback not saved');
  }

  clearFeedbackPendingTimer(pending) {
    if (pending?.timeoutId) {
      clearTimeout(pending.timeoutId);
    }
  }

  rollbackFeedback(clientEventId) {
    const pending = this.pendingFeedbackEvents.get(clientEventId);
    if (!pending) return;

    const currentState = this.feedbackStates.get(pending.messageId);
    if (currentState?.clientEventId !== clientEventId) {
      this.clearFeedbackPendingTimer(pending);
      this.pendingFeedbackEvents.delete(clientEventId);
      return;
    }

    this.clearFeedbackPendingTimer(pending);
    if (pending.previousState) {
      this.feedbackStates.set(pending.messageId, {
        ...pending.previousState,
        pending: false
      });
    } else {
      this.feedbackStates.delete(pending.messageId);
    }

    this.pendingFeedbackEvents.delete(clientEventId);
    this.refreshMessageActionById(pending.messageId);
  }

  applyFeedbackStateToElement(messageId, messageElement) {
    if (!messageElement) return;

    const state = this.feedbackStates.get(String(messageId));
    const buttons = messageElement.querySelectorAll('.miptech-message-action-feedback-positive, .miptech-message-action-feedback-negative');

    buttons.forEach((button) => {
      const isPositive = button.classList.contains('miptech-message-action-feedback-positive');
      const value = isPositive ? 'positive' : 'negative';
      const isMatching = state?.value === value;
      const isPending = isMatching && state?.pending === true;
      const isSelected = isMatching && state?.pending !== true;
      button.classList.toggle('is-selected', isSelected);
      button.classList.toggle('is-pending', isPending);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });
  }

  getMessageElementById(messageId) {
    if (!this.messageContainer) return null;

    const normalizedId = String(messageId);
    const elements = this.messageContainer.querySelectorAll('[data-message-id]');
    return Array.from(elements).find((element) => (
      element.getAttribute('data-message-id') === normalizedId
    )) || null;
  }

  refreshMessageActionById(messageId) {
    const element = this.getMessageElementById(messageId);
    if (!element) return;

    const message = this.messages.find(item => String(item.id) === String(messageId));
    if (message) {
      this.renderMessageActions(message, element);
    }
  }

  refreshMessageActions() {
    if (!this.messageContainer) return;

    const messageElements = this.messageContainer.querySelectorAll('[data-message-id]');
    messageElements.forEach((messageElement) => {
      const messageId = messageElement.getAttribute('data-message-id');
      const message = this.messages.find(item => String(item.id) === String(messageId));
      if (message) {
        this.renderMessageActions(message, messageElement);
      }
    });
  }

  showMessageActionStatusById(messageId, text) {
    const element = this.getMessageElementById(messageId);
    if (element) {
      this.showMessageActionStatus(element, text);
    }
  }

  showMessageActionStatus(messageElement, text) {
    if (!messageElement) return;

    const statusElement = messageElement.querySelector('.miptech-message-action-status');
    if (!statusElement) return;

    statusElement.textContent = text;
    clearTimeout(statusElement._miptechStatusTimer);
    statusElement._miptechStatusTimer = setTimeout(() => {
      statusElement.textContent = '';
    }, 1800);
  }

  async init() {
    if (this.initialized) return;

    try {
      this.log('MipTech Enterprise Widget: Initializing...');

      // Wait for DOM
      if (document.readyState === 'loading') {
        await new Promise(resolve => {
          document.addEventListener('DOMContentLoaded', resolve);
        });
      }

      // Re-resolve consent state (cookie may have changed since constructor)
      this.consentState = this._resolveConsentState();
      this.log('MipTech Enterprise Widget: Consent state:', this.consentState);

      // Shell UI — always render first, before any remote call
      this.renderShellUI();

      // --- GDPR consent branching ---
      if (this.consentState === 'undecided' || this.consentState === 'declined') {
        this.createConsentWindow();
        this.initMobileLayout();
        this.state = STATES.READY;
        this.initialized = true;
        this.log('MipTech Enterprise Widget: Ready (consent pending)');
        this.emit(EVENTS.WIDGET_READY);
        return;
      }
      // --- End GDPR branching ---

      // Handle Preview Mode force-open
      if (this.config.previewMode && this.config.forceOpen) {
        this.isVisible = true;
        this.container.classList.add('is-open');
      }

      // Bootstrap remote session (may fail — UI already visible)
      await this.bootstrapRemoteSession();
      this.initialized = true;

      this.log('MipTech Enterprise Widget: Ready');
      this.emit(EVENTS.WIDGET_READY);

    } catch (error) {
      console.error('MipTech Widget: Initialization failed', error);
      this.state = STATES.ERROR;
      this.initialized = true; // Widget exists, in error state
      this.showInlineError(error);
    }
  }

  /**
   * Render the shell UI (container + toggle button).
   * Idempotent — safe to call multiple times.
   */
  renderShellUI() {
    if (this.container) return;
    this.createContainer();
    this.createToggleButton();
    if (this.toggleButton) {
      this.toggleButton.innerHTML = this.getToggleIconsHTML();
    }
    this.setupEventListeners();
    // Note: initMobileLayout() is called after createChatWindow() in
    // bootstrapRemoteSession() because it accesses this.chatWindow
  }

  /**
   * Bootstrap remote session: auth, chat window, restore, WebSocket.
   * Guarded against concurrent calls via _bootstrapping flag.
   */
  async bootstrapRemoteSession() {
    if (this._bootstrapping) return;
    this._bootstrapping = true;

    try {
      // Close existing socket if present
      if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
        this.socket.close(1000, 'Re-bootstrapping');
      }

      // Preview mode
      if (this.config.previewMode) {
        this.log('MipTech Enterprise Widget: Running in PREVIEW mode (no WebSocket)');
        this.session = {
          token: 'preview-token',
          sessionId: this.generateInstanceId(),
          userId: 'preview-user',
          tenantId: this.config.tenantId
        };
        this.createChatWindow();
        this.state = STATES.PREVIEW;
        return;
      }

      // Determine auth flow
      const hasPreGeneratedToken = this.config.wsUrl && this.config.wsUrl.includes('token=');
      this._isPreGenTokenFlow = hasPreGeneratedToken;
      this.log('bootstrapRemoteSession: hasPreGenToken:', hasPreGeneratedToken, 'hasTokenEndpoint:', !!(this.config.tokenEndpoint || window.MIPTECH_TOKEN_ENDPOINT));

      // 1. Token already present in wsUrl (server-assisted, inline)
      if (hasPreGeneratedToken) {
        this.log('MipTech Enterprise Widget: Using Server-Assisted Pattern (pre-generated token)');
        const urlObj = new URL(this.config.wsUrl, window.location.origin);
        this.session = {
          token: urlObj.searchParams.get('token'),
          sessionId: this.generateInstanceId(),
          userId: this.config.userId || 'guest',
          tenantId: this.config.tenantId
        };

      // 2. tokenEndpoint available + wsUrl base valid → server-assisted fallback
      } else if ((this.config.tokenEndpoint || window.MIPTECH_TOKEN_ENDPOINT) && this.config.wsUrl) {
        this.log('MipTech Enterprise Widget: Using tokenEndpoint bootstrap (server-assisted fallback)');
        const previousChatId = this.session?.chatId;

        try {
          const tokenFromEndpoint = await this.fetchFreshToken();
          if (!tokenFromEndpoint) throw new Error('No token returned from endpoint');

          this._isPreGenTokenFlow = true;
          this._updateWsUrlWithToken(tokenFromEndpoint);
          this.session = {
            token: tokenFromEndpoint,
            sessionId: this.generateInstanceId(),
            userId: this.config.userId || 'guest',
            tenantId: this.config.tenantId
          };
        } catch (err) {
          // tokenEndpoint exists but failed — do NOT fall through to createSession (422)
          throw new Error('Token endpoint bootstrap failed: ' + err.message);
        }

        if (previousChatId && this.session) {
          this.session.chatId = previousChatId;
        }

      // 3. Last resort: standard flow (createSession)
      } else {
        this.log('MipTech Enterprise Widget: Using standard initialization flow');
        const previousChatId = this.session?.chatId;
        await this.loadTenantConfig();
        await this.createSession();
        if (previousChatId && this.session) {
          this.session.chatId = previousChatId;
        }
      }

      // Chat window — idempotent (guard inside createChatWindow)
      this.createChatWindow();
      this.initMobileLayout();

      // Restore chat history only when explicitly enabled.
      if (this.isFeatureEnabled('chatHistory')) {
        this._restoreChat();
      } else {
        this.clearLegacyChatHistoryStorage();
      }

      // Do not reuse a restored chatId for new WebSocket traffic. The backend
      // may skip chat_joined for restored sessions, leaving feature
      // capabilities non-authoritative. Start a fresh server chat after reload
      // while still allowing the local UI history to render.
      this._restoredChatId = null;

      // Auto-open widget if it was open before navigation/reload
      if (this._shouldAutoOpen) {
        this.show({ restoring: true });
        this._shouldAutoOpen = false;
      }

      // Connect WebSocket — don't block on Promise resolution.
      // The onmessage handler processes messages regardless.
      // State transitions happen inside the handler (CONNECTED on join, etc.)
      this.connectWebSocket().catch(err => {
        this.log('WebSocket connection failed during bootstrap:', err);
        this.handleConnectionError();
      });

    } finally {
      this._bootstrapping = false;
    }
  }

  async loadTenantConfig() {
    try {
      const response = await fetch(`${this.config.apiUrl}/api/tenant/${this.config.tenantId}/config`);
      if (response.ok) {
        const tenantConfig = await response.json();

        this.config = {
          ...this.config,
          brandColor: tenantConfig.brandColor || this.config.brandColor,
          accentColor: tenantConfig.accentColor || this.config.accentColor,
          botName: tenantConfig.botName || 'Assistant',
          welcomeMessage: tenantConfig.welcomeMessage || 'How can I help you?',
          features: { ...this.config.features, ...tenantConfig.features }
        };

        this.log('Tenant configuration loaded:', tenantConfig.name);
      }
    } catch (error) {
      console.warn('Could not load tenant config, using defaults:', error.message);
    }
  }

  async createSession() {
    try {
      const response = await fetch(`${this.config.apiUrl}/api/auth/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          tenantId: this.config.tenantId,
          userId: this.config.userId,
          metadata: {
            userAgent: navigator.userAgent,
            url: window.location.href,
            referrer: document.referrer
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Session creation failed: ${response.status}`);
      }

      this.session = await response.json();
      this.log('Session created:', this.session.sessionId);

    } catch (error) {
      throw new Error(`Failed to create session: ${error.message}`);
    }
  }

  /**
   * Connect WebSocket. Returns a Promise that resolves when the connection
   * is truly ready (connection_ready/joined/chat_ready received), or rejects
   * on early close/error. After initial resolution, subsequent close/error
   * events are handled by handleConnectionError().
   */
  connectWebSocket() {
    // Native WebSocket connection
    if (this.config.previewMode) {
      this.state = STATES.PREVIEW;
      this.updateConnectionStatus(STATES.PREVIEW);
      this.log('MipTech Widget: Running in preview mode (no WebSocket)');
      // Inject dummy content for preview
      setTimeout(() => this.injectPreviewContent(), 500);
      return Promise.resolve();
    }

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.close();
    }

    this.state = STATES.CONNECTING;
    this.emit(EVENTS.CONNECTION_STATUS, { status: 'connecting' });

    let wsUrl = this.config.wsUrl;
    let token = this.session.token;

    if (this.config.wsUrl.includes('?token=')) {
      const urlParts = this.config.wsUrl.split('?token=');
      wsUrl = urlParts[0];
      token = urlParts[1];
    }

    const tenantId = this.config.tenantId || this.session.tenantId || '';
    const wsUrlWithParams = `${wsUrl}?tenant_id=${encodeURIComponent(tenantId)}&token=${encodeURIComponent(token)}`;

    this.log('Native WebSocket connection:', { wsUrl, tenantId, hasToken: !!token });

    return new Promise((resolve, reject) => {
      let settled = false;

      // Called when connection is truly ready (after join/ready message)
      const onConnectionReady = () => {
        if (!settled) {
          settled = true;
          resolve();
        }
      };

      // Store resolver so onmessage handler can call it
      this._onConnectionReady = onConnectionReady;

      try {
      this.socket = new WebSocket(wsUrlWithParams);

      this.socket.onopen = () => {
        this.log('WebSocket connected');

        const joinMessage = {
          type: 'join_chat',
          data: {
            channel: 'wordpress',
            create: true
          }
        };

        this.log('Sending join_chat message:', JSON.stringify(joinMessage));
        this.socket.send(JSON.stringify(joinMessage));

        this.reconnectCount = 0;
        this.emit(EVENTS.CONNECTION_STATUS, { status: 'connecting' });

        this.startHeartbeat();
      };

      this.socket.onclose = (event) => {
        this.log('WebSocket disconnected', event.code, event.reason);
        this.state = STATES.DISCONNECTED;
        this.emit(EVENTS.CONNECTION_STATUS, { status: 'disconnected' });
        this.stopHeartbeat();

        if (!settled) {
          // Connection failed before becoming ready
          settled = true;
          reject(new Error(`WebSocket closed before ready: ${event.code} ${event.reason}`));
        } else if (event.code !== 1000 && event.code !== 1001) {
          this.handleConnectionError();
        }
      };

      this.socket.onerror = (error) => {
        console.error('WebSocket error:', error);
        if (!settled) {
          // Will be followed by onclose which rejects
          // Don't reject here to avoid double-rejection
        } else {
          this.handleConnectionError();
        }
      };

      this.socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // V2 Handling (Guided Selling)
          if (message.type === 'product_recommendations' && message.version === '2') {
            const { mode, hero_product_id, alternative_product_ids } = message;
            // V2 assumes products are in message.data.products or we need to map them. 
            // Strategy: If 'data.results' exists (standard RAG), filter and sort them.
            const products = message.data?.results || message.products;

            if (products && Array.isArray(products)) {
              let orderedProducts = [];
              // If Hero Mode, prioritize Hero ID
              if (mode === 'hero' && hero_product_id) {
                const hero = products.find(p => String(p.id) === String(hero_product_id));
                const others = products.filter(p => String(p.id) !== String(hero_product_id));
                if (hero) orderedProducts.push(hero);
                orderedProducts = [...orderedProducts, ...others];
              } else {
                orderedProducts = products;
              }
              // Buffer with V2 metadata
              this.pendingProducts = orderedProducts;
              this.pendingProductsMode = mode;
              this.pendingHeroId = hero_product_id;

              // Same buffering logic as V1
              this.log(`Buffering V2 Product Recs (Mode: ${mode})`);
              if (!this.responseTimeout) {
                this.responseTimeout = setTimeout(() => {
                  if (this.pendingProducts) {
                    this.renderProductRecommendations(this.pendingProducts, this.pendingProductsMode, this.pendingHeroId);
                    this.pendingProducts = null;
                    this.responseTimeout = null;
                  }
                }, 5000);
              }
            }
            return; // Skip default logging to clean up
          }

          // Debug logs for product events
          if (message.type === 'product_recommendations') {
            this.log('DEBUG: Product event received', message);
          }

          this.log('WebSocket message received:', message);

          const eventChatId = this.extractChatIdFromEvent(message);
          if (eventChatId) {
            if (!this.session) {
              this.session = {};
            }
            this.session.chatId = String(eventChatId);
            this.beginLiveChatScope(eventChatId);
            this.log('Captured live chat_id:', eventChatId);
          }

          this.ingestServerCapabilities(message);

          // Auto-promote to CONNECTED if we receive any meaningful server message
          if (this.state !== STATES.CONNECTED && message.type && !['connection_established', 'initialization_progress', 'error', 'pong'].includes(message.type)) {
            this.log('Auto-promoting to CONNECTED on message type:', message.type);
            this.state = STATES.CONNECTED;
            this.emit(EVENTS.CONNECTION_STATUS, { status: 'connected' });
          }

          if (message.type === 'connection_established') {
            this.log('Connection established');
          } else if (message.type === 'initialization_progress') {
            const progressText = message?.data?.message || message?.data?.phase || 'initializing…';
            this.log('Initialization progress:', progressText);
          } else if (message.type === 'connection_ready' || message.type === 'joined' || message.type === 'chat_ready') {
            this.log('Connection ready - creating chat session...');
            this.state = STATES.CONNECTED;
            this.emit(EVENTS.CONNECTION_STATUS, { status: 'connected' });

            if (!this.session.chatId) {
              this.log('Sending new_chat request...');
              this.socket.send(JSON.stringify({
                type: 'new_chat',
                data: {
                  title: 'WordPress Chat',
                  channel: 'wordpress'
                }
              }));
            } else {
              // Resolve connectWebSocket() Promise — connection truly ready
              if (this._onConnectionReady) this._onConnectionReady();
              this.processMessageQueue();
            }
          } else if (message.type === 'chat_created' || message.type === 'chat_joined') {
            this.log('Chat ready:', message.type, message.chat_id);
            // Resolve connectWebSocket() Promise — connection truly ready
            if (this._onConnectionReady) this._onConnectionReady();
            this.processMessageQueue();
          } else if (message.type === 'message_received') {
            this.log('Message received by server:', message.message_id);
          } else if (message.type === 'response_start') {
            this.log('Response generation starting...');
            this.isGeneratingResponse = true; // Flag for streaming state
            this.handleResponseStart(message);
          } else if (message.type === 'response_chunk') {
            this.handleResponseChunk(message);
          } else if (message.type === 'response_complete') {
            this.log('Response generation complete');
            this.handleResponseComplete(message);
            this.isGeneratingResponse = false;

            // Render pending products AFTER text is complete
            if (this.pendingProducts) {
              this.log('Rendering pending products after response complete');
              // Pass V2 metadata if available
              this.renderProductRecommendations(this.pendingProducts, this.pendingProductsMode, this.pendingHeroId);
              this.pendingProducts = null;
              this.pendingProductsMode = null;
              this.pendingHeroId = null;
              if (this.responseTimeout) {
                clearTimeout(this.responseTimeout);
                this.responseTimeout = null;
              }
            }
          } else if (message.type === 'product_recommendations') {
            const products = message.data?.results;
            if (products && Array.isArray(products)) {
              // Always buffer to ensure bottom positioning (after text)
              this.log('Buffering product recommendations (Waiting for response_complete or timeout)');
              this.pendingProducts = products;

              // === V2 Metadata Extraction ===
              // Check for Guided Selling V2 format (mode, hero_product_id)
              if (message.data?.version === '2' || message.data?.mode) {
                this.pendingProductsMode = message.data.mode || 'options';
                this.pendingHeroId = message.data.hero_product_id || null;
                this.log('Guided Selling V2 detected:', {
                  mode: this.pendingProductsMode,
                  heroId: this.pendingHeroId
                });

                // Reorder products to put hero first if in hero mode
                if (this.pendingProductsMode === 'hero' && this.pendingHeroId) {
                  // Debug: Log all product IDs for matching
                  this.log('Hero matching - heroId:', this.pendingHeroId, 'Product IDs:',
                    products.map(p => ({ id: p.id, product_id: p.product_id, sku: p.sku, name: p.name?.substring(0, 30) }))
                  );

                  // Use String coercion for robust ID matching
                  const heroIdStr = String(this.pendingHeroId);
                  const heroProduct = products.find(p =>
                    String(p.id) === heroIdStr ||
                    String(p.product_id) === heroIdStr ||
                    String(p.sku) === heroIdStr
                  );
                  const otherProducts = products.filter(p =>
                    String(p.id) !== heroIdStr &&
                    String(p.product_id) !== heroIdStr &&
                    String(p.sku) !== heroIdStr
                  );

                  if (heroProduct) {
                    this.pendingProducts = [heroProduct, ...otherProducts];
                    this.log('Reordered products: Hero first -', heroProduct.name);
                  } else {
                    console.warn('Hero product NOT FOUND! heroId:', this.pendingHeroId, 'Available IDs:',
                      products.map(p => p.id || p.product_id || p.sku)
                    );
                  }
                }
              } else {
                // Legacy format - no V2 metadata
                this.pendingProductsMode = null;
                this.pendingHeroId = null;
              }

              // Safety: If no response_complete comes in 5s, render.
              if (!this.responseTimeout) {
                this.responseTimeout = setTimeout(() => {
                  if (this.pendingProducts) {
                    this.log('Safety timeout: Rendering pending products');
                    this.renderProductRecommendations(this.pendingProducts, this.pendingProductsMode, this.pendingHeroId);
                    this.pendingProducts = null;
                    this.pendingProductsMode = null;
                    this.pendingHeroId = null;
                    this.responseTimeout = null;
                  }
                }, 5000);
              }
            }
          } else if (message.type === 'message' || message.type === 'chat_message' || message.type === 'assistant_message') {
            this.handleIncomingMessage(message);
          } else if (message.type === 'message_ack' || message.type === 'sent') {
            this.log('Message acknowledged by backend');
          } else if (message.type === 'message_feedback_ack') {
            this.handleFeedbackAck(message);
          } else if (message.type === 'message_feedback_rejected') {
            this.handleFeedbackRejected(message);
          } else if (message.type === 'typing') {
            this.handleTypingIndicator(message.data);
          } else if (message.type === 'pong') {
            this.lastPongTime = Date.now();
          } else if (message.type === 'error') {
            console.error('Backend error:', message.data);
            // Do NOT reset streaming state here — the backend may continue
            // sending chunks after an error (e.g., "streaming interrupted" followed
            // by a retry with the actual response). Let handleResponseChunk and
            // handleResponseComplete manage the streaming lifecycle naturally.
            this.emit(EVENTS.ERROR, { error: message.data });
          } else {
            this.log('Unknown message type:', message.type, message);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error, event.data);
        }
      };

      } catch (error) {
        console.error('Failed to create WebSocket:', error);
        if (!settled) {
          settled = true;
          reject(error);
        }
      }
    }); // end Promise
  }

  startHeartbeat() {
    this.stopHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.socket.send(JSON.stringify({ type: 'ping' }));
      }
    }, this.config.heartbeatInterval || 30000);
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  handleConnectionError() {
    // Single-flight guard: if already reconnecting, ignore
    if (this._reconnectingSocket) return;

    if (this.reconnectCount >= this.config.reconnectAttempts) {
      this.showInlineError(new Error('Connessione persa'));
      return;
    }

    this._reconnectingSocket = true;
    this.reconnectCount++;

    const delay = Math.min(1000 * Math.pow(2, this.reconnectCount), 30000);
    this.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectCount})`);

    this._reconnectTimer = setTimeout(async () => {
      this._reconnectTimer = null;
      try {
        // Determine if token refresh is needed
        // Criteria: based on actual reconnection failure context, not close codes
        const tokenStale =
          this._isPreGenTokenFlow &&
          (this.config.tokenEndpoint || window.MIPTECH_TOKEN_ENDPOINT) &&
          (
            this.reconnectCount > 1 ||       // simple retry already failed
            this._wasStableBeforeDisconnect() // was connected, then dropped
          );

        if (tokenStale) {
          this.log('Token likely stale, refreshing...');
          const newToken = await this.fetchFreshToken();
          if (newToken) this._updateWsUrlWithToken(newToken);
        } else if (!this._isPreGenTokenFlow && this.reconnectCount > 1) {
          // Standard flow: re-auth preserving chatId
          const previousChatId = this.session?.chatId;
          await this.createSession();
          if (previousChatId && this.session) this.session.chatId = previousChatId;
        }

        await this.connectWebSocket();
        // Success: reset counter
        this.reconnectCount = 0;
      } catch (err) {
        this.log('Reconnect attempt failed:', err);
        // Schedule next attempt via setTimeout (not synchronous recursion)
        this._reconnectingSocket = false;
        this.handleConnectionError();
        return;
      }
      this._reconnectingSocket = false;
    }, delay);
  }

  /**
   * Check if the connection was stable before disconnecting.
   * Used to decide whether token refresh is likely needed.
   */
  _wasStableBeforeDisconnect() {
    if (!this.lastPongTime) return false;
    return (Date.now() - this.lastPongTime) > 30000;
  }

  /**
   * Fetch a fresh WebSocket token from the token endpoint.
   * Centralizes the pattern already used in GDPR consent flow (lines 1511-1518).
   */
  async fetchFreshToken() {
    const endpoint = this.config.tokenEndpoint || window.MIPTECH_TOKEN_ENDPOINT;
    if (!endpoint) return null;
    this.log('Fetching fresh token from', endpoint);
    const resp = await fetch(endpoint);
    if (!resp.ok) throw new Error('Token refresh failed: ' + resp.status);
    const data = await resp.json();
    return data.token || data.websocket_token || null;
  }

  /**
   * Update the WebSocket URL with a new token, preserving other query params.
   */
  _updateWsUrlWithToken(token) {
    try {
      const url = new URL(this.config.wsUrl);
      url.searchParams.set('token', token);
      this.config.wsUrl = url.toString();
    } catch (e) {
      // Fallback if URL not parseable
      const base = this.config.wsUrl.split('?')[0];
      this.config.wsUrl = base + '?token=' + encodeURIComponent(token);
    }
    if (this.session) this.session.token = token;
  }

  /**
   * Show an inline error message inside the widget with a retry button.
   * Deduplicated: removes previous error messages before showing a new one.
   */
  showInlineError(_error) {
    // Create chat window if it doesn't exist yet (idempotent)
    if (!this.chatWindow) {
      this.createChatWindow();
      this.initMobileLayout();
    }

    // Remove any previous inline errors (dedupe)
    const existing = this.messageContainer?.querySelectorAll('.miptech-inline-error');
    if (existing) existing.forEach(el => el.remove());

    const errorContainer = document.createElement('div');
    errorContainer.className = 'miptech-inline-error';

    const errorText = document.createElement('p');
    errorText.textContent = 'Connessione persa. Riprova tra qualche istante.';
    errorContainer.appendChild(errorText);

    const retryBtn = document.createElement('button');
    retryBtn.className = 'miptech-retry-btn';
    retryBtn.textContent = 'Riprova';

    retryBtn.addEventListener('click', () => {
      retryBtn.disabled = true;
      retryBtn.textContent = 'Sto riprovando...';

      // Cancel any pending automatic reconnect timer
      if (this._reconnectTimer) {
        clearTimeout(this._reconnectTimer);
        this._reconnectTimer = null;
      }
      this._reconnectingSocket = false;
      this.reconnectCount = 0;

      this.bootstrapRemoteSession()
        .then(() => {
          // bootstrapRemoteSession awaits connectWebSocket which resolves
          // only when the connection is truly ready — safe to remove error
          errorContainer.remove();
        })
        .catch(err => {
          retryBtn.disabled = false;
          retryBtn.textContent = 'Riprova';
          console.warn('Retry failed:', err);
        });
    });

    errorContainer.appendChild(retryBtn);
    if (this.messageContainer) {
      this.messageContainer.appendChild(errorContainer);
      this.scrollToBottom();
    }
  }

  /**
   * @deprecated Use showInlineError() instead.
   * Kept for backwards compatibility — no longer called internally.
   */
  fallbackToSimpleMode() {
    this.log('Falling back to simple mode (deprecated)');
    import('./simple-widget.js').then(({ default: SimpleMIPTechWidget }) => {
      if (this.container) {
        this.container.remove();
      }
      new SimpleMIPTechWidget(this.config);
    }).catch(() => {
      console.error('Could not load fallback widget');
    });
  }

  createContainer() {
    if (this.container) return; // idempotent
    this.container = document.createElement('div');
    this.container.id = `miptech-enterprise-widget-${this.instanceId}`;
    this.container.className = `miptech-widget-container miptech-widget-${this.config.position}`;
    this.container.setAttribute('data-state', this.state);

    this.injectStyles();
    document.body.appendChild(this.container);

  }

  initMobileLayout() {
    this._mobileLastWidth = 0;

    const apply = () => {
      const vw = window.innerWidth;
      const isMobile = vw <= 768;
      this.container.classList.toggle('is-mobile', isMobile);
      if (isMobile) {
        // Applica solo se la larghezza cambia (ignora resize da tastiera che cambia solo altezza)
        if (vw !== this._mobileLastWidth) {
          this._mobileLastWidth = vw;
          this._applyMobileStyles();
        }
      } else {
        this._mobileLastWidth = 0;
        ['position','top','left','right','bottom','width','boxSizing','overflow','pointerEvents','zIndex'].forEach(p => this.container.style[p] = '');
        ['position','bottom','right','pointerEvents'].forEach(p => this.toggleButton.style[p] = '');
        ['position','top','left','right','bottom','width','maxWidth','height','maxHeight','minHeight','borderRadius','pointerEvents']
          .forEach(p => this.chatWindow.style[p] = '');
      }
    };
    apply();
    window.addEventListener('resize', apply);
  }

  /** Apply mobile inline styles to container, toggle, and current chatWindow */
  _applyMobileStyles() {
    Object.assign(this.container.style, {
      position: 'fixed',
      top: '0', left: '0', right: '0', bottom: '0',
      width: '100%', boxSizing: 'border-box', overflow: 'hidden',
      pointerEvents: 'none', zIndex: '999999'
    });
    Object.assign(this.toggleButton.style, {
      position: 'absolute',
      bottom: '20px', right: '20px',
      pointerEvents: 'auto'
    });
    if (this.chatWindow) {
      Object.assign(this.chatWindow.style, {
        position: 'absolute',
        top: '15px', bottom: '15px',
        left: '15px', right: '15px',
        width: 'auto', maxWidth: 'none',
        height: 'auto', maxHeight: 'none', minHeight: '0',
        borderRadius: '16px', pointerEvents: 'auto'
      });
    }
  }

  createToggleButton() {
    this.toggleButton = document.createElement('button');
    this.toggleButton.className = 'miptech-toggle-button';
    this.toggleButton.innerHTML = this.getChatIcon();
    this.toggleButton.title = 'Open chat';
    this.toggleButton.setAttribute('aria-label', 'Open chat');
    this.toggleButton.setAttribute('aria-expanded', 'false');

    const statusIndicator = document.createElement('div');
    statusIndicator.className = 'miptech-status-indicator';
    statusIndicator.setAttribute('data-status', this.state);
    this.toggleButton.appendChild(statusIndicator);

    this.toggleButton.addEventListener('click', () => this.toggle());
    this.container.appendChild(this.toggleButton);
  }

  createChatWindow() {
    if (this.chatWindow) return; // idempotent
    this.chatWindow = document.createElement('div');
    this.chatWindow.className = 'miptech-chat-window';
    if (!this.config.showUserAvatar) {
      this.chatWindow.classList.add('miptech-hide-user-avatar');
    }
    this.chatWindow.style.display = 'none';

    this.chatWindow.innerHTML = `
      <div class="miptech-chat-header">
        <div class="miptech-chat-title">
          ${this.config.headerLogoUrl ? `<img src="${this.config.headerLogoUrl}" class="miptech-header-logo" alt="Logo">` : ''}
          <div class="miptech-title-text">
            <h3>${this.config.botName || 'Chat Support'}</h3>
            <span class="miptech-status ${this.config.previewMode ? 'preview' : (this.state === STATES.CONNECTED ? 'online' : 'offline')}">
              ${this.config.previewMode ? 'Preview' : (this.state === STATES.CONNECTED ? 'Online' : 'Offline')}
            </span>
          </div>
        </div>
        <div class="miptech-header-actions">
          ${this.config.features.videoCall ? '<button class="miptech-action-btn" data-action="video" title="Video call">📹</button>' : ''}
          ${this.config.features.voiceMessage ? '<button class="miptech-action-btn" data-action="voice" title="Voice message">🎤</button>' : ''}
          <button class="miptech-minimize-btn" title="Minimize">
            <svg width="14" height="2" viewBox="0 0 14 2" fill="none"><rect width="14" height="2" rx="1" fill="white"/></svg>
          </button>
        </div>
      </div>

      <div class="miptech-content-card">
        <div class="miptech-chat-messages" id="miptech-messages-${this.instanceId}">
          <div class="miptech-message bot welcome">
            ${this.config.botAvatarUrl ? `<div class="miptech-message-avatar" style="background-color: #ffffff !important; border: 1px solid #f0f0f0;">${this.getAvatarHTML('bot')}</div>` : ''}
            <div class="miptech-message-content">
              <p id="miptech-welcome-msg-${this.instanceId}"></p>
              <span class="miptech-message-time">${this.formatTime(new Date())}</span>
            </div>
          </div>

          <div class="miptech-typing-indicator" id="miptech-typing-${this.instanceId}" style="display: none;">
            <div class="miptech-typing-dots">
              <span></span><span></span><span></span>
            </div>
            <span class="miptech-typing-text">Agent is typing...</span>
          </div>
        </div>

        <div class="miptech-chat-input-wrapper">
          <div class="miptech-chat-input">
            ${this.config.features.fileUpload ? '<button class="miptech-file-btn" title="Attach file">📎</button>' : ''}
            <input type="text" placeholder="Type a message..." id="miptech-input-${this.instanceId}" maxlength="1000">
            <button class="miptech-send-btn" aria-label="Send message">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            </button>
          </div>

          <div class="miptech-footer">
            <a href="https://www.miptechnologies.tech" target="_blank" rel="noopener noreferrer" class="miptech-footer-link">
              <span class="miptech-powered-by">Powered by</span>
              <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=" alt="MIP" class="miptech-logo" id="miptech-logo-${this.instanceId}">
            </a>
          </div>
        </div>
      </div>

      ${this.config.features.fileUpload ? '<input type="file" class="miptech-file-input" style="display: none;" multiple>' : ''}
    `;

    this.messageContainer = this.chatWindow.querySelector(`#miptech-messages-${this.instanceId}`);
    this.inputField = this.chatWindow.querySelector(`#miptech-input-${this.instanceId}`);

    this.setupChatInteractions();
    this.loadMIPLogo();
    this.container.appendChild(this.chatWindow);

    // Trigger typing animation for welcome message
    setTimeout(() => {
      const welcomeEl = this.chatWindow.querySelector(`#miptech-welcome-msg-${this.instanceId}`);
      if (welcomeEl && this.config.welcomeMessage) {
        this.typeText(welcomeEl, this.config.welcomeMessage);
      }
    }, 500);
  }

  typeText(element, text, baseSpeed = 40) {
    let i = 0;
    element.innerHTML = ''; // Ensure empty

    // Create cursor - use block style for typewriter feel
    const cursor = document.createElement('span');
    cursor.className = 'miptech-typing-cursor';
    cursor.innerHTML = '▍';
    element.appendChild(cursor);

    const type = () => {
      if (i < text.length) {
        // Handle HTML tags roughly if present (skip them) or just treat as text
        // For welcome message, assuming plain text is safer for this simple implementation
        const char = text.charAt(i);
        cursor.insertAdjacentText('beforebegin', char);
        i++;

        // Calculate delay: Base + Random Variance
        // This creates a "human" or "mechanical" imperfection
        let delay = baseSpeed + (Math.random() * 50 - 25);

        // Pause on punctuation for rhythm
        if (['.', '!', '?', ','].includes(char)) {
          delay += 250;
        }

        // Prevent negative delay
        setTimeout(type, Math.max(10, delay));
      } else {
        // Keep blinking cursor for a moment to show "done" state, then fade/remove
        setTimeout(() => cursor.remove(), 2000);
      }
    };

    type();
  }

  loadMIPLogo() {
    const logoElement = this.chatWindow.querySelector(`#miptech-logo-${this.instanceId}`);
    if (logoElement && this.config.logoUrl) {
      this.log('Loading MIP logo from:', this.config.logoUrl);
      logoElement.src = this.config.logoUrl;

      logoElement.onerror = () => {
        console.warn('MIP logo failed to load:', this.config.logoUrl);
        logoElement.style.display = 'none';
      };
    }
  }

  setupChatInteractions() {
    // In preview mode, disable all interactions
    if (this.config.previewMode) {
      this.disableInputForPreview();
      return;
    }

    const sendBtn = this.chatWindow.querySelector('.miptech-send-btn');


    const sendMessage = () => {
      const content = this.inputField.value.trim();
      if (content) {
        this.sendMessage(content);
        this.inputField.value = '';
      }
    };

    sendBtn.addEventListener('click', sendMessage);
    this.inputField.addEventListener('keypress', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    });

    if (this.config.features.typingIndicator) {
      this.inputField.addEventListener('input', () => {
        this.handleUserTyping();
      });
    }

    if (this.config.features.fileUpload) {
      const fileBtn = this.chatWindow.querySelector('.miptech-file-btn');
      const fileInput = this.chatWindow.querySelector('.miptech-file-input');

      fileBtn.addEventListener('click', () => fileInput.click());
      fileInput.addEventListener('change', (e) => this.handleFileUpload(e));
    }

    this.chatWindow.querySelectorAll('.miptech-action-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const action = e.target.getAttribute('data-action');
        this.handleActionButton(action);
      });
    });

    // Minimize button - hides the chat window
    const minimizeBtn = this.chatWindow.querySelector('.miptech-minimize-btn');
    if (minimizeBtn) {
      minimizeBtn.addEventListener('click', () => this.hide());
    }
  }

  /**
   * Disable input field and send button for preview mode
   * Called when widget is in admin preview - no actual chat functionality
   */
  disableInputForPreview() {
    if (this.inputField) {
      this.inputField.disabled = true;
      this.inputField.placeholder = 'Preview mode - input disabled';
      this.inputField.style.cursor = 'not-allowed';
      this.inputField.style.opacity = '0.6';
    }

    const sendBtn = this.chatWindow?.querySelector('.miptech-send-btn');
    if (sendBtn) {
      sendBtn.disabled = true;
      sendBtn.style.opacity = '0.4';
      sendBtn.style.cursor = 'not-allowed';
    }

    // Also disable file upload button if present
    const fileBtn = this.chatWindow?.querySelector('.miptech-file-btn');
    if (fileBtn) {
      fileBtn.disabled = true;
      fileBtn.style.opacity = '0.4';
    }

    this.log('MipTech Widget: Input disabled for preview mode');
  }

  sendMessage(content, type = 'text', metadata = {}) {
    if (!this.session.chatId) {
      this.log('No chat_id yet - queueing message');
      this.messageQueue.push({ content, type, metadata });
      return;
    }

    const message = {
      type: 'chat_message',
      data: {
        chat_id: this.session.chatId,
        message: content,
        stream: true,
        channel: 'wordpress'
      }
    };

    this.log('Sending user message:', JSON.stringify(message));

    this.addMessageToUI({
      id: this.generateMessageId(),
      content: content,
      messageType: type,
      metadata,
      timestamp: new Date().toISOString(),
      senderId: this.session?.userId || 'user',
      senderType: 'user'
    });

    if (this.state === STATES.CONNECTED && this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
      this.emit(EVENTS.MESSAGE_SENT, message);
    } else {
      this.messageQueue.push({ content, type, metadata });
      this.log('Message queued (not connected)');
    }
  }

  handleIncomingMessage(message) {
    const uiMessage = this.normalizeLiveAssistantMessage(message, {
      content: message.data?.content || message.data?.text || message.content || '',
      messageType: message.data?.messageType || 'text',
      timestamp: message.event_ts ? new Date(message.event_ts).toISOString() : new Date().toISOString()
    });

    this.log('Rendering assistant message:', uiMessage.content);
    this.addMessageToUI(uiMessage);
    this.emit(EVENTS.MESSAGE_RECEIVED, uiMessage);
  }

  handleResponseStart(message) {
    this.log('Starting new streaming response');

    this.streamingContent = '';

    this.streamingMessage = this.normalizeLiveAssistantMessage(message, {
      content: '',
      messageType: 'text',
      timestamp: new Date().toISOString(),
      isStreaming: true,
      status: 'streaming'
    });

    this.streamingMessageId = this.streamingMessage.id;

    this.addStreamingMessageToUI(this.streamingMessage);
  }

  handleResponseChunk(message) {
    const chunk = message.data?.chunk || message.data?.content || message.data?.text || message.data?.delta || '';

    if (!chunk) {
      console.warn('Response chunk received but no content found:', message);
      return;
    }

    this.log('Response chunk:', chunk);

    if (!this.streamingMessage) {
      console.warn('Received chunk without response_start, creating message');
      this.handleResponseStart(message);
    }

    this.streamingContent += chunk;
    this.streamingMessage.content = this.streamingContent;

    this.updateStreamingMessage(this.streamingMessage);
  }

  handleResponseComplete(message) {
    this.log('Streaming response complete, final content:', this.streamingContent);

    if (!this.streamingMessage) {
      console.warn('Response complete but no streaming message exists');
      return;
    }

    this.promoteStreamingMessageId(message);

    this.streamingMessage.isStreaming = false;
    this.streamingMessage.status = 'complete';

    const finalMessageId = this.streamingMessage?.id;

    this.finalizeStreamingMessage(this.streamingMessage);

    this.messages.push(this.streamingMessage);

    if (finalMessageId) {
      this.refreshMessageActionById(finalMessageId);
    }

    // Persist completed streaming message to chatHistory
    if (!this._restoring) {
      this.chatHistory.push({
        type: 'message',
        data: {
          id: this.streamingMessage.id,
          idSource: this.streamingMessage.idSource,
          role: 'assistant',
          content: this.streamingMessage.content,
          timestamp: this.streamingMessage.timestamp,
          senderType: this.streamingMessage.senderType || 'bot',
          status: 'complete'
        },
        ts: Date.now()
      });
      this._persistChat();
    }

    this.emit(EVENTS.MESSAGE_RECEIVED, this.streamingMessage);

    this.streamingMessage = null;
    this.streamingMessageId = null;
    this.streamingContent = '';
  }

  promoteStreamingMessageId(message) {
    const persistedId = this.getAssistantMessageIdFromEvent(message);
    if (!persistedId || !this.streamingMessage) return;

    const previousId = this.streamingMessage.id;
    if (String(previousId) === String(persistedId)) {
      this.streamingMessage.idSource = 'backend';
      this.attachLiveChatMetadata(this.streamingMessage);
      return;
    }

    const messageElement = this.getMessageElementById(previousId);
    if (messageElement) {
      messageElement.setAttribute('data-message-id', persistedId);
    }

    if (previousId) {
      this._renderedMessageIds.delete(previousId);
    }
    this._renderedMessageIds.add(persistedId);

    this.streamingMessage.id = persistedId;
    this.streamingMessage.idSource = 'backend';
    this.streamingMessageId = persistedId;
    this.attachLiveChatMetadata(this.streamingMessage);
  }

  addStreamingMessageToUI(message) {
    const messageElement = document.createElement('div');
    messageElement.className = `miptech-message ${message.senderType}`;
    messageElement.setAttribute('data-message-id', message.id);
    messageElement.setAttribute('data-streaming', 'true');

    const avatar = this.getAvatarHTML(message.senderType);
    const time = this.formatTime(new Date(message.timestamp));

    const avatarHTML = this.config.botAvatarUrl
      ? `<div class="miptech-message-avatar" style="background-color: #ffffff !important; border: 1px solid #f0f0f0;">${avatar}</div>`
      : '';

    messageElement.innerHTML = `
      ${avatarHTML}
      <div class="miptech-message-content">
        <p class="miptech-streaming-content"></p>
        <span class="miptech-typing-cursor">▋</span>
        <div class="miptech-message-meta">
          <span class="miptech-message-time">${time}</span>
        </div>
      </div>
    `;

    this.messageContainer.appendChild(messageElement);
    this.scrollToBottom();
  }

  updateStreamingMessage(message) {
    const messageElement = this.messageContainer.querySelector(
      `[data-message-id="${message.id}"]`
    );

    if (!messageElement) {
      console.warn('Could not find message element to update:', message.id);
      return;
    }

    const contentElement = messageElement.querySelector('.miptech-streaming-content');
    if (contentElement) {
      // Use innerHTML with markdown parsing instead of textContent
      contentElement.innerHTML = this.parseMarkdown(message.content);
      this.scrollToBottom();
    }
  }

  finalizeStreamingMessage(message) {
    const messageElement = this.messageContainer.querySelector(
      `[data-message-id="${message.id}"]`
    );

    if (!messageElement) {
      console.warn('Could not find message element to finalize:', message.id);
      return;
    }

    messageElement.removeAttribute('data-streaming');

    const cursor = messageElement.querySelector('.miptech-typing-cursor');
    if (cursor) {
      cursor.remove();
    }

    this.renderMessageActions(message, messageElement);
    this.log('Finalized streaming message:', message.id, 'Final length:', message.content.length);
  }

  addMessageToUI(message) {
    // Dedupe: skip if this message ID was already rendered
    if (message.id && this._renderedMessageIds.has(message.id)) {
      this.log('Skipping duplicate message:', message.id);
      return;
    }
    if (message.id) this._renderedMessageIds.add(message.id);

    const messageElement = document.createElement('div');
    messageElement.className = `miptech-message ${message.senderType}`;
    messageElement.setAttribute('data-message-id', message.id);

    const avatar = this.getAvatarHTML(message.senderType);
    const time = this.formatTime(new Date(message.timestamp));

    const avatarBgStyle = message.senderType === 'bot' || message.senderType === 'assistant'
      ? 'style="background-color: #ffffff !important; border: 1px solid #f0f0f0;"'
      : '';

    const showAvatar = message.senderType === 'bot'
      ? !!this.config.botAvatarUrl
      : !!this.config.userAvatarUrl;
    const avatarHTML = showAvatar
      ? `<div class="miptech-message-avatar" ${avatarBgStyle}>${avatar}</div>`
      : '';

    messageElement.innerHTML = `
      ${avatarHTML}
      <div class="miptech-message-content">
        ${this.renderMessageContent(message)}
        ${message.senderType !== 'user' ? `<div class="miptech-message-meta"><span class="miptech-message-time">${time}</span></div>` : ''}
        ${this.config.features.readReceipts ? '<span class="miptech-message-status">✓</span>' : ''}
      </div>
    `;

    this.renderMessageActions(message, messageElement);
    this.messageContainer.appendChild(messageElement);
    this.scrollToBottom();

    this.messages.push(message);

    // Persist to chatHistory (skip during restore to avoid loops)
    if (!this._restoring) {
      this.chatHistory.push({
        type: 'message',
        data: {
          id: message.id, role: message.role, content: message.content,
          idSource: message.idSource,
          timestamp: message.timestamp, senderType: message.senderType,
          status: message.status || 'complete'
        },
        ts: Date.now()
      });
      this._persistChat();
    }
  }

  // FIX CRITICO: Nuovo metodo per scroll automatico
  scrollToBottom() {
    requestAnimationFrame(() => {
      if (this.messageContainer) {
        this.messageContainer.scrollTop = this.messageContainer.scrollHeight;
      }
    });
  }

  renderMessageContent(message) {
    switch (message.type) {
      case 'file':
        return `<div class="miptech-file-message">
          <span class="miptech-file-icon">📎</span>
          <a href="${message.content}" target="_blank">${message.metadata.fileName}</a>
        </div>`;
      case 'image':
        return `<img src="${message.content}" alt="Shared image" class="miptech-message-image">`;
      default:
        // Use parseMarkdown which handles escaping internally
        return `<p>${this.parseMarkdown(message.content)}</p>`;
    }
  }

  // Helper to parse basic markdown (Bold only for now as requested)
  parseMarkdown(text) {
    if (!text) return '';
    // 1. Escape HTML to prevent XSS (Security)
    let safeText = this.escapeHtml(text);

    // 2. Block-level processing (headers, lists)
    const lines = safeText.split('\n');
    const result = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      // Headers: # to ###### Title → bold block
      const headerMatch = line.match(/^#{1,6}\s+(.+)$/);
      if (headerMatch) {
        result.push(`<strong style="display:block;margin:8px 0 4px">${headerMatch[1]}</strong>`);
        i++;
        continue;
      }

      // Numbered list item: "1. item" → styled div with original number
      const numMatch = line.match(/^(\d+)\.\s+(.+)$/);
      if (numMatch) {
        result.push(`<div style="margin:2px 0;padding-left:8px"><strong>${numMatch[1]}.</strong> ${numMatch[2]}</div>`);
        i++;
        continue;
      }

      // Bullet list: "- item" or "   - item" (with optional leading whitespace)
      const bulletMatch = line.match(/^\s*-\s+(.+)$/);
      if (bulletMatch) {
        const items = [];
        while (i < lines.length && /^\s*-\s+/.test(lines[i])) {
          items.push('<li>' + lines[i].replace(/^\s*-\s+/, '') + '</li>');
          i++;
        }
        result.push('<ul style="margin:2px 0;padding-left:20px">' + items.join('') + '</ul>');
        continue;
      }

      // Empty line → skip (avoids double <br>)
      if (line.trim() === '') {
        result.push('');
        i++;
        continue;
      }

      // Regular line
      result.push(line);
      i++;
    }

    // 3. Join lines, then apply inline formatting
    safeText = result.join('<br>');

    // Bold: **text** → <strong>
    safeText = safeText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // Italic: *text* → <em> (avoid matching inside **)
    safeText = safeText.replace(/(?<!\*)\*(?!\*)(.*?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');

    // Clean up excessive <br> sequences
    safeText = safeText.replace(/(<br>){3,}/g, '<br><br>');
    safeText = safeText.replace(/(<\/ul>|<\/div>|<\/strong>)<br><br>/g, '$1<br>');
    safeText = safeText.replace(/<br>(<ul|<div style="margin:2px|<strong style="display:block)/g, '$1');

    return safeText;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  handleUserTyping() {
    if (!this.config.features.typingIndicator || this.state !== STATES.CONNECTED) return;

    if (!this.isTyping) {
      this.isTyping = true;
    }

    clearTimeout(this.typingTimeout);
    this.typingTimeout = setTimeout(() => {
      this.isTyping = false;
    }, 1000);
  }

  handleTypingIndicator(data) {
    const typingIndicator = this.chatWindow.querySelector(`#miptech-typing-${this.instanceId}`);
    if (data.isTyping && data.userId !== this.session?.userId) {
      typingIndicator.style.display = 'flex';
      this.scrollToBottom();
    } else {
      typingIndicator.style.display = 'none';
    }
  }

  async handleFileUpload(event) {
    const files = Array.from(event.target.files);

    for (const file of files) {
      if (file.size > this.config.maxFileSize) {
        alert(`File ${file.name} is too large. Maximum size: ${this.formatFileSize(this.config.maxFileSize)}`);
        continue;
      }

      const fileUrl = URL.createObjectURL(file);

      if (file.type.startsWith('image/')) {
        this.sendMessage(fileUrl, 'image', { fileName: file.name, fileSize: file.size });
      } else {
        this.sendMessage(fileUrl, 'file', { fileName: file.name, fileSize: file.size });
      }
    }

    event.target.value = '';
  }

  handleActionButton(action) {
    switch (action) {
      case 'video':
        this.log('Video call requested');
        break;
      case 'voice':
        this.log('Voice message requested');
        break;
    }
  }

  processMessageQueue() {
    this.log('Processing message queue, length:', this.messageQueue.length);
    while (this.messageQueue.length > 0) {
      const queued = this.messageQueue.shift();
      this.sendMessage(queued.content, queued.type, queued.metadata);
    }
  }

  show(options = {}) {
    if (this.isVisible) return;
    if (!this.chatWindow) return; // chatWindow not yet created (bootstrap in progress)

    this.isVisible = true;
    this.chatWindow.style.display = 'flex';
    this.toggleButton.innerHTML = this.getCloseIcon();
    this.toggleButton.setAttribute('aria-expanded', 'true');
    this.toggleButton.classList.add('is-open');

    requestAnimationFrame(() => {
      this.chatWindow.style.transform = 'translateY(0)';
      this.chatWindow.style.opacity = '1';
    });

    this.updateConnectionStatusUI();

    // Skip focus/scroll during restore — avita tastiera mobile e scroll aggressivo
    if (!options.restoring) {
      setTimeout(() => {
        this.inputField?.focus();
        this.scrollToBottom();
      }, 300);
    }

    this.emit(EVENTS.WIDGET_OPENED);
    this._persistChat();
  }

  hide() {
    if (!this.isVisible) return;
    if (!this.chatWindow) return;

    this.isVisible = false;
    this.chatWindow.style.transform = 'translateY(20px)';
    this.chatWindow.style.opacity = '0';
    this.toggleButton.innerHTML = this.getToggleIconsHTML();
    this.toggleButton.setAttribute('aria-expanded', 'false');
    this.toggleButton.classList.remove('is-open');

    setTimeout(() => {
      this.chatWindow.style.display = 'none';
    }, 300);

    this.emit(EVENTS.WIDGET_CLOSED);
    this._persistChat();
  }

  toggle() {
    if (!this.isVisible && this.consentState === 'declined') {
      // Re-consent: user clicked launcher after declining.
      // Reset UI only (cookie stays 'declined' until user actually accepts).
      this.consentSubmitting = false;
      const acceptBtn = this.chatWindow && this.chatWindow.querySelector('.miptech-consent-accept-btn');
      if (acceptBtn) { acceptBtn.textContent = 'Accept All'; acceptBtn.disabled = false; acceptBtn.style.opacity = '1'; }
      const declineBtn = this.chatWindow && this.chatWindow.querySelector('.miptech-consent-decline-btn');
      if (declineBtn) { declineBtn.disabled = false; declineBtn.style.opacity = '1'; }
      const errorEl = this.chatWindow && this.chatWindow.querySelector('.miptech-consent-error');
      if (errorEl) errorEl.style.display = 'none';
    }
    this.isVisible ? this.hide() : this.show();
  }

  // ============================================================
  // GDPR In-Widget Consent
  // ============================================================

  _resolveConsentState() {
    if (!this.config.gdprRequired) return 'accepted';

    // Source of truth: cookie client-side
    const cookieData = this._readConsentCookie();
    if (cookieData && this._isConsentValid(cookieData)) {
      if (cookieData.level === 'full') return 'accepted';
      if (cookieData.level === 'declined') return 'declined';
    }

    // Fallback: config injected by PHP at page load
    if (this.config.gdprConsent === 'full') return 'accepted';
    if (this.config.gdprConsent === 'declined') return 'declined';

    return 'undecided';
  }

  _readConsentCookie() {
    const cfg = this.config.gdprConfig || {};
    const name = cfg.cookieName || 'miptech_consent';
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    if (!match) return null;
    return this._parseConsentCookie(decodeURIComponent(match[1]));
  }

  _parseConsentCookie(raw) {
    try {
      const data = JSON.parse(raw);
      if (typeof data === 'object' && data !== null && data.level) return data;
      return null;
    } catch (e) {
      return null;
    }
  }

  _isConsentValid(data) {
    if (!data || !data.level || !data.version) return false;
    const cfg = this.config.gdprConfig || {};
    const expectedVersion = cfg.consentVersion || '1.0';
    if (data.version !== expectedVersion) return false;
    if (!['full', 'declined'].includes(data.level)) return false;
    return true;
  }

  _setConsentCookie(level) {
    const cfg = this.config.gdprConfig || {};
    const data = JSON.stringify({
      level: level,
      timestamp: Math.floor(Date.now() / 1000),
      version: cfg.consentVersion || '1.0'
    });
    const path = cfg.cookiePath || '/';
    const domain = cfg.cookieDomain ? '; domain=' + cfg.cookieDomain : '';
    const secure = cfg.cookieSecure ? '; Secure' : '';
    const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
    const name = cfg.cookieName || 'miptech_consent';

    document.cookie = name + '=' + encodeURIComponent(data) +
      '; path=' + path + domain + secure +
      '; expires=' + expires + '; SameSite=Lax';
  }

  _logConsentServerSide(level) {
    // Best-effort telemetry. Non-blocking, non-functional.
    // Failure does not affect UI or flow.
    if (!this.config.ajaxUrl || !this.config.consentNonce) return;
    try {
      fetch(this.config.ajaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: 'action=miptech_update_consent&consent_level=' + level +
          '&nonce=' + encodeURIComponent(this.config.consentNonce),
        keepalive: true
      }).catch(() => {});
    } catch (e) { /* silenzioso */ }
  }

  _escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  createConsentWindow() {
    this.chatWindow = document.createElement('div');
    this.chatWindow.className = 'miptech-chat-window miptech-consent-window';
    this.chatWindow.style.display = 'none';

    const cfg = this.config.gdprConfig || {};
    const brandColor = this.config.brandColor || '#007cba';
    const privacyUrl = cfg.privacyPolicyUrl || '';
    const miptechUrl = 'https://www.miptechnologies.tech';
    const miptechPrivacyUrl = 'https://www.miptechnologies.tech/legal/privacy-policy';
    const consentText = cfg.consentText || 'To provide you with the best experience, our AI assistant needs to collect and use certain information.';
    const logoUrl = this.config.logoUrl || this.config.headerLogoUrl || '';

    this.chatWindow.innerHTML = `
      <div class="miptech-chat-header">
        <div class="miptech-chat-title">
          ${this.config.headerLogoUrl ? `<img src="${this._escapeHTML(this.config.headerLogoUrl)}" class="miptech-header-logo" alt="Logo">` : ''}
          <div class="miptech-title-text">
            <h3>${this._escapeHTML(this.config.botName || 'Chat Support')}</h3>
          </div>
        </div>
        <div class="miptech-header-actions">
          <button class="miptech-minimize-btn" title="Close" aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1 1L13 13M13 1L1 13" stroke="white" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      </div>
      <div class="miptech-consent-body">
        <div class="miptech-consent-icon-shield">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z" fill="${brandColor}" opacity="0.15"/>
            <path d="M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z" stroke="${brandColor}" stroke-width="1.5" fill="none"/>
            <path d="M9 12l2 2 4-4" stroke="${brandColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h4 class="miptech-consent-heading">We Value Your Privacy!</h4>
        <p class="miptech-consent-text">${this._escapeHTML(consentText)} For more details, please visit our <a href="${miptechPrivacyUrl}" target="_blank" rel="noopener noreferrer" class="miptech-consent-link-privacy">Privacy Policy</a>.</p>
        <p class="miptech-consent-error" style="display:none"></p>
        <div class="miptech-consent-actions">
          <button class="miptech-consent-accept-btn" style="background:${brandColor}">Accept All Cookies</button>
          <button class="miptech-consent-decline-btn">Reject</button>
        </div>
        <a href="${miptechUrl}" target="_blank" rel="noopener noreferrer" class="miptech-consent-powered">
          Powered by <img src="${this._escapeHTML(logoUrl || '')}" alt="MipTech" class="miptech-consent-powered-logo" onerror="this.style.display='none';this.nextSibling.style.display='inline'"><span style="display:none">MipTech</span>
        </a>
      </div>
    `;

    this.container.appendChild(this.chatWindow);

    // Event listeners
    this.chatWindow.querySelector('.miptech-consent-accept-btn')
      .addEventListener('click', () => this.handleConsentAccept());
    this.chatWindow.querySelector('.miptech-consent-decline-btn')
      .addEventListener('click', () => this.handleConsentDecline());
    this.chatWindow.querySelector('.miptech-minimize-btn')
      .addEventListener('click', () => this.hide());
  }

  async handleConsentAccept() {
    if (this.consentSubmitting) return;
    this.consentSubmitting = true;

    const btns = this.chatWindow.querySelectorAll('button');
    btns.forEach(b => { b.disabled = true; b.style.opacity = '0.5'; });

    const acceptBtn = this.chatWindow.querySelector('.miptech-consent-accept-btn');
    if (acceptBtn) acceptBtn.textContent = 'Connecting...';

    const errorEl = this.chatWindow.querySelector('.miptech-consent-error');
    if (errorEl) errorEl.style.display = 'none';

    try {
      // 1. Set cookie
      this._setConsentCookie('full');
      this.consentState = 'accepted';

      // 2. Fetch token from REST endpoint
      const tokenEndpoint = this.config.tokenEndpoint || window.MIPTECH_TOKEN_ENDPOINT;
      if (!tokenEndpoint) throw new Error('No token endpoint configured');

      this.log('MipTech GDPR: Fetching token from', tokenEndpoint);
      const resp = await fetch(tokenEndpoint);
      if (!resp.ok) throw new Error('Token fetch failed: ' + resp.status);
      const data = await resp.json();
      const token = data.token || data.websocket_token;
      if (!token) throw new Error('No token in response');

      // 3. Transition to chat
      await this._startChatAfterConsent(token);

      // 4. Best-effort server-side logging
      this._logConsentServerSide('full');

    } catch (error) {
      console.error('MipTech GDPR: Consent accept failed:', error);
      if (acceptBtn) acceptBtn.textContent = 'Retry';
      btns.forEach(b => { b.disabled = false; b.style.opacity = '1'; });
      this.consentSubmitting = false;

      if (errorEl) {
        errorEl.textContent = 'Unable to connect. Please try again.';
        errorEl.style.display = 'block';
      }
    }
  }

  async _startChatAfterConsent(token) {
    // Update config with token
    const wsBase = this.config.wsUrl.split('?')[0];
    this.config.wsUrl = wsBase + '?token=' + encodeURIComponent(token);
    this.session = {
      token: token,
      sessionId: this.generateInstanceId(),
      userId: this.config.userId || 'guest',
      tenantId: this.config.tenantId
    };

    // Transition UI: consent window → chat window
    const wasVisible = this.isVisible;
    this.chatWindow.remove();
    this.chatWindow = null; // Reset so createChatWindow() idempotency guard allows recreation
    this.createChatWindow();
    // Re-apply mobile styles to the new chat window
    if (this.container.classList.contains('is-mobile')) {
      this._applyMobileStyles();
    }
    this.setupEventListeners();
    if (wasVisible) {
      this.isVisible = false;
      this.show();
    }

    // Connect WebSocket — don't await, let it connect in background
    // The onmessage handler works regardless of Promise resolution
    this.connectWebSocket().then(() => {
      this.state = STATES.READY;
      this.log('MipTech GDPR: Chat started after consent');
    }).catch(err => {
      console.error('MipTech GDPR: WebSocket connection failed after consent:', err);
      this.handleConnectionError();
    });
  }

  handleConsentDecline() {
    this._setConsentCookie('declined');
    this.consentState = 'declined';
    this.hide();
    // Launcher stays visible — user can re-open to change their mind
    this._logConsentServerSide('declined');
  }

  setupEventListeners() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible) {
        this.hide();
      }
    });

    this.on(EVENTS.CONNECTION_STATUS, (data) => {
      const statusIndicator = this.toggleButton.querySelector('.miptech-status-indicator');
      const statusText = this.chatWindow.querySelector('.miptech-status');

      if (statusIndicator) {
        statusIndicator.setAttribute('data-status', data.status);
      }

      if (statusText) {
        statusText.className = `miptech-status ${data.status === 'connected' ? 'online' : 'offline'}`;
        statusText.textContent = data.status === 'connected' ? 'Online' : 'Connecting...';
      }
    });
  }

  updateConnectionStatusUI() {
    const statusIndicator = this.toggleButton?.querySelector('.miptech-status-indicator');
    const statusText = this.chatWindow?.querySelector('.miptech-status');

    const isConnected = this.state === STATES.CONNECTED;

    if (statusIndicator) {
      statusIndicator.setAttribute('data-status', isConnected ? 'connected' : 'connecting');
    }

    if (statusText) {
      statusText.className = `miptech-status ${isConnected ? 'online' : 'offline'}`;
      statusText.textContent = isConnected ? 'Online' : 'Connecting...';
    }
  }

  generateInstanceId() {
    return `miptech_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  generateMessageId() {
    return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  formatTime(date) {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }

  /**
   * Get avatar HTML for bot or user
   * @param {string} type - 'bot' or 'user'
   * @returns {string} HTML string for avatar
   */
  getAvatarHTML(type) {
    if (type === 'bot') {
      return this.config.botAvatarUrl ?
        `<img src="${this.escapeHtml(this.config.botAvatarUrl)}" alt="Bot" style="display:block; width:100%; height:100%; object-fit:cover;" />` :
        '';
    } else {
      return this.config.userAvatarUrl ?
        `<img src="${this.escapeHtml(this.config.userAvatarUrl)}" alt="User" style="display:block; width:100%; height:100%; object-fit:cover;" />` :
        '';
    }
  }

  getBotSvg() {
    return `<svg width="24" height="24" viewBox="0 0 24 24" fill="${this.config.brandColor}"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-14h2v6h-2zm0 8h2v2h-2z"/></svg>`;
  }

  getUserSvg() {
    return `<svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>`;
  }


  formatFileSize(bytes) {
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Convert hex color to RGB for rgba() usage
  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
    }
    return '0, 124, 186'; // Fallback to default brand color
  }

  getToggleIconsHTML() {
    return `
      <div class="miptech-icon-mask">
        <div class="miptech-icon-carousel">
          <div class="miptech-icon-slide">${this.getChatIcon()}</div>
          <div class="miptech-icon-slide">${this.getEditIcon()}</div>
        </div>
      </div>
    `;
  }

  getEditIcon() {
    return `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
      </svg>
    `;
  }

  getChatIcon() {
    return `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
      </svg>
    `;
  }

  getCloseIcon() {
    return `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
      </svg>
    `;
  }

  injectStyles() {
    if (document.getElementById('miptech-enterprise-widget-styles')) return;

    const style = document.createElement('style');
    style.id = 'miptech-enterprise-widget-styles';
    style.textContent = `
      /* === BASE CONTAINER === */
      .miptech-widget-container {
        position: fixed;
        z-index: 999999;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        font-size: 18px;
        line-height: 1.4;
      }

      .miptech-widget-bottom-right {
        bottom: 20px;
        right: 20px;
      }

      .miptech-widget-bottom-left {
        bottom: 20px;
        left: 20px;
      }

      /* === TOGGLE BUTTON - MINIMAL CAROUSEL DESIGN === */
      .miptech-toggle-button {
        position: relative;
        width: 60px;
        height: 60px;
        border-radius: 50%;
        border: none;
        background: ${this.config.brandColor};
        color: white;
        cursor: pointer;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        transition: transform 0.3s ease, box-shadow 0.3s ease, background-color 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden; /* Mask for carousel */
        z-index: 1000000;
      }

      .miptech-toggle-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
      }

      .miptech-toggle-button.is-open {
        transform: rotate(90deg);
        background: ${this.config.brandColor}; /* Use brand color as base */
        filter: brightness(0.85); /* Slightly darker */
      }

      /* Carousel container */
      .miptech-icon-mask {
        width: 24px;
        height: 24px;
        overflow: hidden;
      }

      .miptech-icon-carousel {
        display: flex;
        flex-direction: column;
        transition: transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
      }

      /* Hover interaction: Slide up */
      .miptech-toggle-button:not(.is-open):hover .miptech-icon-carousel {
        transform: translateY(-24px);
      }

      /* Individual icon slide */
      .miptech-icon-slide {
        height: 24px;
        width: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .miptech-toggle-button svg {
        width: 24px;
        height: 24px;
      }

      /* Status indicator with improved visibility */
      .miptech-status-indicator {
        position: absolute;
        top: 6px;
        right: 6px;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        border: 2px solid white;
        transition: all 0.3s ease;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        z-index: 2;
      }

      .miptech-status-indicator[data-status="connected"] {
        background: #4CAF50;
        box-shadow: 0 0 8px rgba(76, 175, 80, 0.5);
      }

      .miptech-status-indicator[data-status="connecting"] {
        background: #FF9800;
        animation: statusPulse 1.5s infinite;
      }

      .miptech-status-indicator[data-status="disconnected"],
      .miptech-status-indicator[data-status="error"] {
        background: #f44336;
      }

      @keyframes statusPulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.6; transform: scale(0.9); }
      }

      /* === CHAT WINDOW - REP AI STYLE === */
      .miptech-chat-window {
        position: absolute;
        bottom: 75px; /* Closer to toggle button */
        right: 0;
        width: 480px;
        height: auto; /* Allow growing */
        min-height: 750px; /* Start like a 24" screen widget */
        max-height: calc(100vh - 140px); /* Grow up to this ceiling (slightly lower than before) */
        background: ${this.config.brandColor}; /* Full card color */
        border-radius: 20px;
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
        display: flex;
        flex-direction: column;
        opacity: 0;
        transform: translateY(20px);
        transition: all 0.3s ease;
        overflow: hidden;
      }

      /* === HEADER === */
      .miptech-chat-header {
        background: transparent;
        color: white;
        padding: 24px 24px 20px 24px; /* Increased bottom padding */
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-shrink: 0;
        position: relative;
      }

      .miptech-chat-title {
         display: flex;
         align-items: center;
         gap: 12px;
         flex: 1;
      }

      .miptech-title-text {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        gap: 2px;
      }

      .miptech-header-logo {
        height: 55px; /* Increased for better visibility */
        width: auto;
        max-width: 160px;
        object-fit: contain;
      }

      .miptech-chat-title h3 {
        margin: 0;
        font-size: 18px; /* Slightly reduced to fit */
        font-weight: 700;
        letter-spacing: -0.3px;
        line-height: 1.2;
      }

      .miptech-status {
        font-size: 11px;
        opacity: 0.9;
        display: flex;
        align-items: center;
      }

      .miptech-status.online:before {
        content: "●";
        color: #4CAF50;
        margin-right: 4px;
      }

      .miptech-status.offline:before {
        content: "●";
        color: #ff6b6b;
        margin-right: 4px;
      }

      .miptech-header-actions {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .miptech-action-btn, 
      .miptech-close-btn {
        background: rgba(255, 255, 255, 0.2);
        border: none;
        color: white;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background 0.2s;
        font-size: 16px;
      }

      .miptech-action-btn:hover,
      .miptech-close-btn:hover,
      .miptech-minimize-btn:hover {
        background: rgba(255, 255, 255, 0.3);
      }

      .miptech-minimize-btn {
        background: rgba(255, 255, 255, 0.2);
        border: none;
        color: white;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background 0.2s;
      }

      /* === CONTENT CARD === */
      .miptech-content-card {
        background: white;
        border-radius: 24px 24px 16px 16px;
        margin: 0 4px 4px 4px; /* Reduced from 6px to 4px for thinner border */
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }

      /* === INPUT WRAPPER - MINIMAL DESIGN === */
      .miptech-chat-input-wrapper {
        flex-shrink: 0;
        background: white;
        padding: 16px 24px;
        border-top: 1px solid #f0f0f0;
      }

      .miptech-chat-input {
        display: flex;
        align-items: center;
        gap: 12px;
        background: white;
        border: 1px solid #e0e0e0;
        border-radius: 50px;
        padding: 6px 6px 6px 20px;
        transition: all 0.2s;
      }

      .miptech-chat-input:focus-within {
        border-color: ${this.config.brandColor};
        box-shadow: 0 0 0 3px rgba(${this.hexToRgb(this.config.brandColor)}, 0.1);
      }

      .miptech-chat-input input {
        flex: 1;
        border: none;
        background: transparent;
        padding: 8px 0;
        height: 40px;
        outline: none;
        font-size: 16px;
        color: #333;
      }

      /* === MESSAGES CONTAINER === */
      .miptech-chat-messages {
        flex: 1;
        min-height: 0;
        padding: 24px;
        overflow-y: auto;
        overflow-x: hidden;
        background: white;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* IE/Edge */
      }

      /* Scrollbar Styling - Hidden */
      .miptech-chat-messages::-webkit-scrollbar {
        display: none; /* Chrome/Safari */
        width: 0;
        height: 0;
      }

      /* === MESSAGES === */
      .miptech-message {
        display: flex;
        margin-bottom: 24px;
        max-width: 100%;
        align-items: flex-end; /* Align avatars to bottom */
        animation: messageSlideIn 0.3s ease;
      }

      @keyframes messageSlideIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }

      .miptech-message.bot {
        margin-right: 48px; /* Align right edge with user bubble (avatar 36px + margins 12px) */
      }

      .miptech-message.user {
        margin-left: auto;
        flex-direction: row-reverse;
      }

      /* When user avatar is hidden */
      .miptech-hide-user-avatar .miptech-message.user .miptech-message-avatar {
        display: none !important;
      }
      .miptech-hide-user-avatar .miptech-message.bot {
        margin-right: 0;
      }

      .miptech-message-avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        margin: 0 6px;
        flex-shrink: 0;
        overflow: hidden;
        /* Default avatars */
        background: ${this.config.brandColor}; /* Use brand color for bot avatar bg */
      }
      
      .miptech-message.user .miptech-message-avatar {
        background: #ccc; /* Visible grey background for user */
        /* display: none; Removed to show user avatar */
      }

      /* Minimal Bot Message */
      .miptech-message.bot .miptech-message-content {
        background: transparent;
        box-shadow: none;
        padding: 0;
        color: #1a1a1a;
        font-size: 15px;
        line-height: 1.5;
        flex: 1;
        min-width: 0;
      }

      /* Minimal User Message */
      .miptech-message.user .miptech-message-content {
        background: ${this.config.brandColor};
        color: white;
        padding: 12px 18px;
        border-radius: 18px 18px 4px 18px;
        box-shadow: none;
        font-size: 14px;
      }

      .miptech-message-content p {
        margin: 0;
        word-wrap: break-word;
      }

      /* Fix for Bold Text Rendering */
      .miptech-message-content strong,
      .miptech-message-content b {
        font-weight: 700 !important;
        color: inherit;
      }

      .miptech-message-content ol,
      .miptech-message-content ul {
        margin: 4px 0;
        padding-left: 20px;
      }

      .miptech-message-content li {
        margin-bottom: 2px;
        line-height: 1.4;
      }

      .miptech-message-content em {
        font-style: italic;
      }

      .miptech-message-time {
        font-size: 11px;
        opacity: 0.5;
        display: block;
        line-height: 1;
      }

      .miptech-message-meta {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        width: 100%;
        min-height: 28px;
        margin-top: 6px;
      }
      
      .miptech-message.user .miptech-message-time {
        color: rgba(255,255,255,0.8);
        text-align: right;
        opacity: 1;
      }

      .miptech-message-status {
        display: none; /* Hide read receipts for minimal look */
      }

      .miptech-message-actions {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        min-height: 28px;
        margin-left: auto;
      }

      .miptech-message-action {
        width: 28px;
        height: 28px;
        border: 1px solid transparent;
        border-radius: 6px;
        background: transparent;
        color: #5f6673;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        cursor: pointer;
        transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
      }

      .miptech-message-action:hover,
      .miptech-message-action:focus-visible {
        background: rgba(17, 24, 39, 0.06);
        border-color: rgba(17, 24, 39, 0.08);
        color: ${this.config.brandColor};
        transform: translateY(-1px);
        outline: none;
      }

      .miptech-message-action svg {
        width: 16px;
        height: 16px;
        fill: none;
        stroke: currentColor;
        stroke-width: 1.55;
        stroke-linecap: round;
        stroke-linejoin: round;
      }

      .miptech-message-action.is-selected {
        background: rgba(${this.hexToRgb(this.config.brandColor) || '0, 124, 186'}, 0.1);
        border-color: rgba(${this.hexToRgb(this.config.brandColor) || '0, 124, 186'}, 0.22);
        color: ${this.config.brandColor};
      }

      .miptech-message-action.is-pending {
        opacity: 0.72;
      }

      .miptech-message-action-status {
        min-width: 1px;
        margin-left: 4px;
        font-size: 11px;
        color: #6b7280;
        line-height: 1;
      }

      /* === STREAMING MESSAGES === */
      .miptech-streaming-content {
        margin: 0;
        word-wrap: break-word;
        white-space: pre-wrap;
        line-height: 1.5;
      }

      .miptech-typing-cursor {
        display: inline-block;
        color: ${this.config.brandColor};
        font-weight: bold;
        animation: cursorBlink 1s infinite;
        margin-left: 2px;
        font-size: 16px;
      }

      @keyframes cursorBlink {
        0%, 50% { opacity: 1; }
        51%, 100% { opacity: 0; }
      }

      [data-streaming="true"] .miptech-message-content {
        opacity: 0.95;
      }

      [data-streaming="true"] .miptech-streaming-content {
        min-height: 1em;
      }

      .miptech-file-message {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .miptech-file-icon {
        font-size: 16px;
      }

      .miptech-message-image {
        max-width: 200px;
        max-height: 200px;
        border-radius: 8px;
        cursor: pointer;
      }

      /* === MOBILE RESPONSIVE (via JS class .is-mobile) === */
      /* Doppia classe = specificità più alta dei singoli selettori base — no !important needed */

      /* Container full-screen, area trasparente non intercetta click */
      .miptech-widget-container.is-mobile {
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100% !important;
        height: 100% !important;
        box-sizing: border-box !important;
        overflow: hidden !important;
        pointer-events: none;
      }

      /* Toggle button riposizionato in basso a destra */
      .miptech-widget-container.is-mobile .miptech-toggle-button {
        position: absolute;
        bottom: calc(20px + env(safe-area-inset-bottom, 0px));
        right: 20px;
        pointer-events: auto;
      }

      /* Nascondi toggle quando chat aperta */
      .miptech-widget-container.is-mobile .miptech-toggle-button.is-open {
        display: none;
      }

      /* Chat window: occupa il container full-screen con margini 15px */
      .miptech-widget-container.is-mobile .miptech-chat-window {
        position: absolute !important;
        top: 15px !important;
        left: 15px !important;
        right: 15px !important;
        bottom: 15px !important;
        width: auto !important;
        max-width: none !important;
        height: auto !important;
        max-height: none !important;
        min-height: 0 !important;
        border-radius: 16px;
        pointer-events: auto;
      }

      /* Header compatto */
      .miptech-widget-container.is-mobile .miptech-chat-header {
        padding: 10px 14px;
        min-height: 52px;
      }

      .miptech-widget-container.is-mobile .miptech-chat-title h3 {
        font-size: 15px;
      }

      /* Messaggi: meno padding */
      .miptech-widget-container.is-mobile .miptech-chat-messages {
        padding: 14px;
      }

      .miptech-widget-container.is-mobile .miptech-message-content {
        font-size: 14px;
      }

      /* Input: ridotto */
      .miptech-widget-container.is-mobile .miptech-chat-input-wrapper {
        padding: 10px 14px;
        padding-bottom: max(10px, calc(10px + env(safe-area-inset-bottom, 0px)));
      }

      .miptech-widget-container.is-mobile .miptech-chat-input input {
        height: 34px;
        font-size: 15px;
      }

      /* === RESPONSIVE FOR LOW HEIGHT SCREENS === */
      @media (min-width: 769px) and (max-height: 900px) {
        .miptech-chat-window {
          height: 650px;
          max-height: calc(100vh - 100px);
        }
      }

      /* === FOOTER AND LOGO FIX === */
      .miptech-footer {
        padding: 8px 16px;
        background: transparent;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        margin-top: 4px;
      }

      /* === RESPONSIVE FOR SMALL LAPTOPS (13" / Low Height) === */
      @media (max-height: 800px) {
        .miptech-chat-window {
          min-height: 450px !important;
          max-height: calc(100vh - 90px) !important;
          bottom: 75px !important;
          width: 420px !important;
        }

        /* Scale Text Down */
        .miptech-chat-header {
          padding: 16px 20px; /* Reduced padding */
        }
        .miptech-header-logo {
          height: 45px; /* Smaller logo */
        }
        .miptech-chat-title h3 {
          font-size: 16px;
        }
        .miptech-message-content {
          font-size: 14px !important; /* Scale message text */
        }
        .miptech-product-name {
          font-size: 14px;
        }
        .miptech-product-price {
          font-size: 14px;
        }
        .miptech-product-image {
          height: 180px; /* Reduce from 210px to fit */
        }
        .miptech-chat-input input {
          font-size: 14px;
          height: 36px;
        }
        .miptech-product-card {
           margin: 0 7.5%; /* Maintain centering */
        }
      }

      .miptech-footer-link {
        display: flex;
        align-items: center;
        gap: 6px;
        text-decoration: none;
        cursor: pointer;
        transition: opacity 0.2s ease;
      }
      .miptech-footer-link:hover {
        opacity: 0.7;
      }

      .miptech-powered-by {
        font-size: 11px;
        color: #999;
        line-height: 1;
      }

      .miptech-logo {
        height: 18px !important; /* Increased size */
        width: auto !important;
        max-width: 80px; /* Increased max-width */
        display: block;
        object-fit: contain;
      }

      /* === SEND BUTTON MINIMAL === */
      .miptech-send-btn {
        background: transparent;
        color: ${this.config.brandColor};
        border: none;
        width: 36px;
        height: 36px;
        flex-shrink: 0;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
        padding: 0;
      }

      .miptech-send-btn:hover {
        background: transparent;
        transform: scale(1.1);
      }

      .miptech-send-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      /* === INLINE ERROR & RETRY === */
      .miptech-inline-error {
        text-align: center;
        padding: 16px;
        margin: 8px 0;
        color: #666;
        font-size: 13px;
      }
      .miptech-inline-error p {
        margin: 0 0 12px 0;
      }
      .miptech-retry-btn {
        background: ${this.config.brandColor};
        color: white;
        border: none;
        border-radius: 6px;
        padding: 8px 20px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        transition: opacity 0.2s;
      }
      .miptech-retry-btn:hover {
        opacity: 0.85;
      }
      .miptech-retry-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      /* === ABORTED MESSAGE INDICATOR === */
      .miptech-aborted-indicator {
        display: block;
        font-size: 11px;
        color: #e67e22;
        margin-top: 4px;
        font-style: italic;
      }

      /* === AVATAR ALIGNMENT FIX (NUCLEAR OPTION) === */
      .miptech-message-avatar {
        width: 36px;
        height: 36px;
        min-width: 36px;
        border-radius: 50%;
        position: relative !important; /* For absolute centering */
        margin: 0 12px 2px 12px;
        flex-shrink: 0;
        overflow: hidden;
        background-clip: padding-box;
        border: 2px solid transparent; /* Optional visual ring */
      }

      /* Default avatars */
      .miptech-message.bot .miptech-message-avatar {
        background: #ffffff !important; /* Force white background */
        border: 1px solid #f0f0f0;
      }
      
      .miptech-message.user .miptech-message-avatar {
         background: #e0e0e0;
      }

      /* Remove background when avatar contains an image */
      .miptech-message-avatar img,
      .miptech-message-avatar svg {
        display: block !important;
        position: absolute !important;
        top: 50% !important;
        left: 50% !important;
        transform: translate(-50%, -50%) !important;
        width: 100% !important;
        height: 100% !important;
        object-fit: cover !important;
        object-position: center center !important;
        border-radius: 50% !important;
        margin: 0 !important;
        padding: 0 !important;
        max-width: none !important;
      }
      
      /* SVG Specifics */
      .miptech-message-avatar svg {
        width: 60% !important; /* Smaller icon inside circle */
        height: 60% !important;
        border-radius: 0 !important;
        object-fit: contain !important;
      }

      /* === PRODUCT CARDS (ARROW NAVIGATION) === */
      .miptech-carousel-wrapper {
        position: relative;
        margin: 8px 0;
        width: 100%;
        /* NO perspective - approccio pseudo-3D semplificato */
      }

      .miptech-product-recs {
        display: flex;
        overflow-x: scroll;
        gap: 0;
        padding: 12px 8%;
        margin: 0;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        scroll-snap-type: x mandatory;
        scroll-padding: 0 8%;
        border-radius: 12px;
        cursor: grab;
      }

      .miptech-product-recs.miptech-dragging {
        cursor: grabbing;
        scroll-behavior: auto;
        scroll-snap-type: none;
        user-select: none;
        -webkit-user-select: none;
      }

      /* Spacers to allow first/last card to be centered by scroll-snap */
      .miptech-product-recs::before,
      .miptech-product-recs::after {
        content: '';
        flex: 0 0 13%;
      }

      .miptech-product-recs::-webkit-scrollbar {
        display: none;
      }

      .miptech-product-card {
        flex: 0 0 70%; /* Ridotto per garantire spazio visibile uniforme alle card laterali */
        scroll-snap-align: center;
        background: white;
        border: 1px solid #eee;
        border-radius: 12px;
        overflow: hidden;
        cursor: pointer;
        display: flex;
        flex-direction: column;
        color: inherit;
        text-decoration: none;
        box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        position: relative;
        margin: 0 2%; /* Ridotto proporzionalmente */

        /* Pseudo-3D: scale + opacity + brightness per depth cues */
        transition: all 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        will-change: transform, opacity, filter;

        /* Default: side card (piccola, trasparente, scura) */
        transform: scale(0.75) translateX(0);
        opacity: 0.5;
        filter: brightness(0.85);

        /* Side cards non cliccabili */
        pointer-events: none;
      }

      /* Active card: centro (grande, opaca, luminosa) */
      .miptech-product-card.miptech-card-active {
        transform: scale(1) translateX(0);
        opacity: 1;
        filter: brightness(1);
        z-index: 10;
        box-shadow: 0 16px 40px rgba(0,0,0,0.2);

        /* Solo card attiva è cliccabile */
        pointer-events: auto;
      }

      /* Hover only on active card */
      .miptech-product-card.miptech-card-active:hover {
        box-shadow: 0 20px 48px rgba(0,0,0,0.25);
      }

      /* Hero card glow animation */
      @keyframes heroGlow {
        0%, 100% {
          box-shadow: 0 0 15px rgba(${this.hexToRgb(this.config.brandColor)}, 0.3),
                      0 0 30px rgba(${this.hexToRgb(this.config.brandColor)}, 0.1);
        }
        50% {
          box-shadow: 0 0 25px rgba(${this.hexToRgb(this.config.brandColor)}, 0.5),
                      0 0 50px rgba(${this.hexToRgb(this.config.brandColor)}, 0.2);
        }
      }

      .miptech-product-card.is-hero.miptech-card-active {
        border: 2px solid ${this.config.brandColor};
        animation: heroGlow 2.5s ease-in-out infinite;
      }

      .miptech-carousel-arrow {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 40px; /* Larger hit area */
        height: 40px;
        background: transparent; /* Minimal: No background */
        border: none;
        box-shadow: none;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        z-index: 10;
        color: ${this.config.brandColor};
        transition: all 0.2s;
        opacity: 0.6; /* Subtle by default */
      }

      .miptech-carousel-arrow.prev {
        left: -8px; /* Slightly outside or on edge */
      }

      .miptech-carousel-arrow.next {
        right: -8px;
      }

      .miptech-carousel-arrow:hover {
        background: transparent;
        color: ${this.config.brandColor};
        transform: translateY(-50%) scale(1.2);
        opacity: 1; /* "Illumina" effect */
        filter: drop-shadow(0 0 4px rgba(255,255,255,0.5));
      }

      .miptech-carousel-arrow svg {
        width: 28px; /* Larger, clearer icon */
        height: 28px;
        fill: currentColor;
      }

      .miptech-product-image {
        width: 100%;
        height: 210px; /* Maximize image space */
        object-fit: contain;
        background: #fff;
        border-bottom: 1px solid #f0f0f0;
        padding: 8px; /* Slightly less padding */
        box-sizing: border-box;
      }

      .miptech-product-info {
        padding: 12px; /* Reduced from 16px */
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        flex-grow: 1;
        gap: 4px; /* Reduced gap between name/price/btn */
      }

      .miptech-product-name {
        font-size: 15px;
        font-weight: 700;
        color: #1a1a1a;
        margin-bottom: 2px; /* Compact margin */
        line-height: 1.2;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .miptech-product-price {
        font-size: 16px;
        font-weight: 600;
        color: ${this.config.brandColor};
        margin-bottom: 4px;
      }

      .miptech-product-cta {
        margin-top: auto;
        padding: 7px 14px;
        background: white;
        border: 1px solid ${this.config.brandColor};
        color: ${this.config.brandColor};
        border-radius: 50px;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        transition: all 0.2s;
        text-align: center;
        box-sizing: border-box;
      }

      .miptech-product-card:hover .miptech-product-cta {
        background: ${this.config.brandColor};
        color: white;
      }

      .miptech-qty-selector {
        display: flex;
        align-items: center;
        gap: 0;
        border: 1px solid #e0e0e0;
        border-radius: 20px;
        overflow: hidden;
        height: 32px;
        flex-shrink: 0;
      }

      .miptech-qty-btn {
        width: 28px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        cursor: pointer;
        font-size: 14px;
        font-weight: 600;
        color: #666;
        transition: all 0.15s;
        padding: 0;
        line-height: 1;
      }

      .miptech-qty-btn:hover {
        background: ${this.config.brandColor}10;
        color: ${this.config.brandColor};
      }

      .miptech-qty-btn:active {
        transform: scale(0.9);
      }

      .miptech-qty-value {
        width: 24px;
        text-align: center;
        font-size: 13px;
        font-weight: 600;
        color: #333;
        border-left: 1px solid #e0e0e0;
        border-right: 1px solid #e0e0e0;
        line-height: 32px;
        user-select: none;
      }

      /* === GDPR CONSENT SCREEN === */
      .miptech-consent-body {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 28px 24px 16px;
        text-align: center;
        flex: 1;
        background: #fff;
        overflow-y: auto;
      }
      .miptech-consent-icon-shield {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: #f8f9fa;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        margin-bottom: 16px;
      }
      .miptech-consent-heading {
        font-size: 17px;
        font-weight: 700;
        margin: 0 0 8px;
        color: #1a1a1a;
      }
      .miptech-consent-text {
        font-size: 13px;
        color: #666;
        margin: 0 0 12px;
        line-height: 1.55;
      }
      .miptech-consent-link-privacy {
        font-size: 12.5px;
        color: #888;
        text-decoration: none;
        margin-bottom: 16px;
        display: inline-block;
        transition: color 0.2s;
      }
      .miptech-consent-link-privacy:hover {
        color: #555;
        text-decoration: underline;
      }
      .miptech-consent-actions {
        display: flex;
        flex-direction: column;
        gap: 8px;
        width: 100%;
      }
      .miptech-consent-accept-btn {
        padding: 12px;
        border: none;
        border-radius: 8px;
        color: #fff;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: opacity 0.2s, transform 0.1s;
        box-shadow: 0 2px 6px rgba(0,0,0,0.15);
      }
      .miptech-consent-accept-btn:hover {
        opacity: 0.92;
        transform: translateY(-1px);
      }
      .miptech-consent-accept-btn:active {
        transform: translateY(0);
      }
      .miptech-consent-accept-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
      }
      .miptech-consent-decline-btn {
        padding: 9px;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        background: transparent;
        color: #888;
        font-size: 13px;
        cursor: pointer;
        transition: background 0.2s, color 0.2s;
      }
      .miptech-consent-decline-btn:hover {
        background: #f5f5f5;
        color: #666;
      }
      .miptech-consent-powered {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        margin-top: 16px;
        font-size: 12px;
        color: #666;
        text-decoration: none;
        transition: color 0.2s;
      }
      .miptech-consent-powered:hover {
        color: #333;
      }
      .miptech-consent-powered-logo {
        height: 16px;
        width: auto;
        opacity: 0.85;
        transition: opacity 0.2s;
      }
      .miptech-consent-powered:hover .miptech-consent-powered-logo {
        opacity: 1;
      }
      .miptech-consent-link-privacy {
        color: inherit;
        font-weight: 600;
        text-decoration: underline;
      }
      .miptech-consent-error {
        color: #dc3545;
        font-size: 13px;
        margin-top: 12px;
      }

    `;

    document.head.appendChild(style);
  }

  on(event, callback) {
    document.addEventListener(event, callback);
  }

  off(event, callback) {
    document.removeEventListener(event, callback);
  }

  emit(event, data = {}) {
    const customEvent = new CustomEvent(event, {
      detail: { ...data, widget: this }
    });
    document.dispatchEvent(customEvent);
  }

  getVersion() {
    return this.version;
  }

  isReady() {
    return this.initialized && this.state === STATES.READY;
  }

  getConnectionStatus() {
    return this.state;
  }

  getMessages() {
    return [...this.messages];
  }

  clearMessages() {
    this.messages = [];
    this.messageContainer.innerHTML = '';
  }

  // ====== Chat History Persistence ======

  /**
   * Get storage key namespaced by tenant and visitor.
   */
  _getChatStorageKey() {
    const visitorId = this._getOrCreateVisitorId();
    return `miptech_chat_v1_${this.config.tenantId}_${visitorId}`;
  }

  /**
   * Get or create a stable visitor ID (persists across sessions via localStorage).
   */
  _getOrCreateVisitorId() {
    if (this.config.userId && this.config.userId !== 'guest') return this.config.userId;
    const KEY = `miptech_vid_${this.config.tenantId}`;
    try {
      let id = localStorage.getItem(KEY);
      if (!id) {
        id = 'v_' + Date.now().toString(36) + Math.random().toString(36).substr(2, 6);
        localStorage.setItem(KEY, id);
      }
      return id;
    } catch (e) {
      return 'anonymous';
    }
  }

  /**
   * Persist chat history to sessionStorage.
   * Cap: max 50 entries, max 200KB. Called after every UI mutation (not on unload).
   */
  _persistChat() {
    if (!this.isFeatureEnabled('chatHistory')) return;
    if (this._restoring || this._storageDegraded) return;
    try {
      const key = this._getChatStorageKey();

      // Cap: last 50 entries
      let history = this.chatHistory.slice(-50);

      const data = {
        version: 2,
        storageSchemaVersion: CHAT_HISTORY_SCHEMA_VERSION,
        chatId: this.session?.chatId,
        history,
        isOpen: this.isVisible,
        savedAt: Date.now()
      };

      let serialized = JSON.stringify(data);

      // Dimensional cap: max 200KB — progressive pruning
      const MAX_BYTES = 200 * 1024;
      while (serialized.length > MAX_BYTES && history.length > 5) {
        history = history.slice(Math.floor(history.length * 0.2));
        data.history = history;
        serialized = JSON.stringify(data);
      }

      sessionStorage.setItem(key, serialized);
    } catch (e) {
      console.warn('MipTech: Chat persistence failed', e);
      this._storageDegraded = true;
    }
  }

  /**
   * Restore chat history from sessionStorage. Runs once only.
   * Keeps restored chatId out of the live session so the next connection
   * receives fresh chat capabilities.
   */
  _restoreChat() {
    if (!this.isFeatureEnabled('chatHistory')) return false;
    if (this._chatRestored) return false;
    this._chatRestored = true;

    try {
      const key = this._getChatStorageKey();
      const raw = sessionStorage.getItem(key);
      if (!raw) return false;
      const data = JSON.parse(raw);

      // Discard old storage versions (v1 had incomplete product data)
      if (!data.version || data.version < 2) {
        sessionStorage.removeItem(key);
        return false;
      }

      if (data.storageSchemaVersion !== CHAT_HISTORY_SCHEMA_VERSION) {
        sessionStorage.removeItem(key);
        return false;
      }

      // TTL: discard if older than 1 hour
      if (Date.now() - data.savedAt > 3600000) {
        sessionStorage.removeItem(key);
        return false;
      }

      this._restoredChatId = null;

      // Restore open state
      if (data.isOpen) {
        this._shouldAutoOpen = true;
      }

      this._restoring = true;

      for (const entry of data.history) {
        if (entry.type === 'message') {
          this.addMessageToUI(entry.data);
        } else if (entry.type === 'products') {
          this.renderProductRecommendations(
            entry.data.products, entry.data.mode, entry.data.heroId
          );
        }
      }

      this._restoring = false;

      // Populate chatHistory from stored data so _persistChat() won't overwrite with []
      this.chatHistory = data.history.slice();

      this.log('Chat history restored:', data.history.length, 'entries');
      return true;
    } catch (e) {
      this._restoring = false;
      console.warn('MipTech: Chat restore failed', e);
      return false;
    }
  }

  clearLegacyChatHistoryStorage() {
    try {
      const keysToRemove = [];
      const currentKey = this._getChatStorageKey();

      if (currentKey) {
        keysToRemove.push(currentKey);
      }

      for (let index = 0; index < sessionStorage.length; index += 1) {
        const key = sessionStorage.key(index);
        if (key && key.startsWith('miptech_chat_')) {
          keysToRemove.push(key);
        }
      }

      Array.from(new Set(keysToRemove)).forEach((key) => {
        sessionStorage.removeItem(key);
      });
    } catch (e) {
      console.warn('MipTech: Chat history cleanup failed', e);
      this._storageDegraded = true;
    }

    this.chatHistory = [];
    this._restoredChatId = null;
  }

  /**
   * Clear persisted chat history. Called on explicit "new chat" actions only.
   * NOT called on destroy() — chat must survive page navigation/refresh.
   */
  clearChatHistory() {
    try {
      const key = this._getChatStorageKey();
      sessionStorage.removeItem(key);
    } catch (e) { /* silent */ }
    this.chatHistory = [];
    this._renderedMessageIds.clear();
    this._chatRestored = false;
    this._storageDegraded = false;
  }

  destroy() {
    this.stopHeartbeat();

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.close(1000, 'Widget destroyed');
    }

    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }

    const styles = document.getElementById('miptech-enterprise-widget-styles');
    if (styles) {
      styles.remove();
    }

    this.initialized = false;
    this.state = 'destroyed';
    this.log('MipTech Enterprise Widget: Destroyed');
  }

  /* === PREVIEW CONTENT INJECTOR === */
  injectPreviewContent() {
    // 1. Welcome Message
    const welcome = this.config.welcomeMessage || "Ciao! Come posso aiutarti oggi?";
    this.addMessageToUI({
      id: 'preview-welcome',
      role: 'bot',
      content: welcome,
      timestamp: new Date().toISOString(),
      senderType: 'bot'
    });

    // 2. User Question (Fake)
    setTimeout(() => {
      this.addMessageToUI({
        id: 'preview-user-q',
        role: 'user',
        content: "Sto cercando un vino per un aperitivo...",
        timestamp: new Date().toISOString(),
        senderType: 'user'
      });
    }, 1000);

    // 3. Bot Response + Hero Card (Fake)
    setTimeout(() => {
      this.addMessageToUI({
        id: 'preview-bot-ans',
        role: 'bot',
        content: "Ti consiglio questo Prosecco, perfetto per iniziare!",
        timestamp: new Date().toISOString(),
        senderType: 'bot'
      });

      // Fake Products for Preview
      const fakeProducts = [
        { id: '101', name: 'Valdobbiadene Prosecco Superiore DOCG', price: 12.50, image_url: 'https://via.placeholder.com/150', url_prodotto: '#' },
        { id: '102', name: 'Prosecco DOC Treviso Brut', price: 9.90, image_url: 'https://via.placeholder.com/150', url_prodotto: '#' },
        { id: '103', name: 'Rosé Millesimato', price: 10.50, image_url: 'https://via.placeholder.com/150', url_prodotto: '#' }
      ];
      // In preview we simulate 'Hero' mode
      this.renderProductRecommendations(fakeProducts, 'hero', '101');
    }, 2000);
  }

  // --- Add-to-Cart capability detection & handlers ---

  _detectCartCapability(cartUrl) {
    if (this._cartCapability) return this._cartCapability;

    try {
      const storeOrigin = new URL(cartUrl).origin;
      const widgetOrigin = window.location.origin;

      if (storeOrigin === widgetOrigin) {
        this.log('Cart capability: same-origin detected, AJAX eligible');
        this._cartCapability = 'ajax';
      } else {
        this.log('Cart capability: cross-origin detected, redirect only',
          { store: storeOrigin, widget: widgetOrigin });
        this._cartCapability = 'redirect';
      }
    } catch (e) {
      this.log('Cart capability: URL parse failed, defaulting to redirect');
      this._cartCapability = 'redirect';
    }

    return this._cartCapability;
  }

  _resolveCartMode(cartUrl) {
    const configured = this.config.addToCartMode || 'auto';
    if (configured === 'redirect') return 'redirect';
    if (configured === 'ajax') return 'ajax';
    return this._detectCartCapability(cartUrl);
  }

  // --- Product normalization & CTA resolution (contract-driven) ---

  _usesProductRedirectSafeMode() {
    return this.isFeatureEnabled('productRedirectSafeMode') ||
      this.config?.productRedirectSafeMode === true;
  }

  _normalizeProduct(product) {
    const meta = product.metadata || {};
    const productUrl = product.url_prodotto || product.url || product.link || product.product_url
      || meta.url_prodotto || meta.url || meta.link || null;
    let storeBaseUrl = null;
    if (productUrl) {
      try { storeBaseUrl = new URL(productUrl).origin; } catch (e) { /* ignore */ }
    }
    return {
      id: product.woo_product_id || product.id || null,
      wooProductId: product.woo_product_id || null,  // Explicit WC product ID for cart v2 gating
      name: product.name,
      productUrl,
      productType: product.product_type || product.type || meta.type || null,
      ctaType: product.cta_type || meta.cta_type || null,
      directPurchasable: product.direct_purchasable ?? null,
      storeBaseUrl,
      cart: product.cart || null,
      parentProductId: product.parent_product_id || product.parent_id || meta.parent_id || null,
      rawCartUrl: product.add_to_cart_url || meta.add_to_cart_url || null,
      cartPayload: product.add_to_cart_payload || meta.add_to_cart_payload || null
    };
  }

  _resolveCTA(normalized) {
    const type = (normalized.productType || '').toLowerCase();

    // Cart v2 on WordPress/WooCommerce: the plugin server-side is the source of truth.
    // Skip client-side redirect safe mode and triple-confirmation; the server will decide.
    // Gating: wooProductId must be a valid WC ID + productUrl must exist + provider must be woocommerce.
    if (this._isCartV2Available()) {
      const wooId = Number(normalized.wooProductId);
      const hasWooId = Number.isFinite(wooId) && wooId > 0;
      if (hasWooId && normalized.productUrl) {
        // Show cart button. The server decides added/redirect/error.
        return { kind: 'add_to_cart', label: null, href: null };
      }
      // No valid WC product ID or no product URL: fall through to backend contract
    }

    // 1. Tenant safe mode (legacy client-side path, when cart v2 is NOT active)
    if (this._usesProductRedirectSafeMode()) {
      if (type === 'variable') {
        return { kind: 'view_options', label: 'Vedi opzioni', href: normalized.productUrl };
      }
      return { kind: 'go_to_product', label: 'Vai al prodotto', href: normalized.productUrl };
    }

    // 2. Backend contract (per tenant non in safe mode)
    if (normalized.ctaType === 'go_to_product') {
      return { kind: 'go_to_product', label: 'Vai al prodotto', href: normalized.productUrl };
    }
    if (normalized.ctaType === 'view_options') {
      return { kind: 'view_options', label: 'Vedi opzioni', href: normalized.productUrl };
    }
    if (
      normalized.ctaType === 'add_to_cart' &&
      normalized.directPurchasable === true &&
      normalized.rawCartUrl
    ) {
      return { kind: 'add_to_cart', label: null, href: normalized.rawCartUrl };
    }

    // 3. Fallback: lascia la logica legacy
    return null;
  }

  _handleCartRedirect(cartUrl, currentQty, cartPayload = null, newTab = true) {
    const wcMatch = cartUrl.match(/[?&]add-to-cart=(\d+)/);
    if (!wcMatch || !wcMatch[1]) {
      // Fallback: navigazione diretta GET se non è URL WooCommerce
      const url = new URL(cartUrl, window.location.href);
      url.searchParams.set('quantity', String(currentQty));
      this.log('Cart redirect (GET fallback):', url.toString());
      if (newTab) window.open(url.toString(), '_blank');
      else window.location.assign(url.toString());
      return;
    }

    const urlId = wcMatch[1];
    const parsedUrl = new URL(cartUrl);
    const formAction = parsedUrl.origin + (parsedUrl.pathname || '/');

    // Allowlist stretta — solo campi WooCommerce essenziali
    const fields = {};
    const qty = String(currentQty);

    if (cartPayload && typeof cartPayload === 'object') {
      const parentId = cartPayload.product_id || null;
      const variationId = cartPayload.variation_id || null;

      if (variationId && parentId) {
        // Variation: campi minimi per add-to-cart
        fields['add-to-cart'] = String(parentId);
        fields['product_id'] = String(parentId);
        fields['variation_id'] = String(variationId);
        fields['quantity'] = qty;
      } else {
        // Simple con payload
        fields['add-to-cart'] = String(urlId);
        fields['quantity'] = qty;
      }
    } else {
      // Simple senza payload
      fields['add-to-cart'] = String(urlId);
      fields['quantity'] = qty;
    }

    // Crea form POST nascosto — replica il comportamento nativo WooCommerce
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = formAction;
    form.style.display = 'none';

    // Pre-apri tab nel gesture context per evitare popup blocker
    if (newTab) {
      const tabName = 'miptech_cart_' + Date.now();
      window.open('about:blank', tabName);
      form.target = tabName;
    }

    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    }

    this.log('Cart POST form submit:', formAction, fields);

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  }

  async _handleCartAjax(cartUrl, currentQty) {
    const wcMatch = cartUrl.match(/[?&]add-to-cart=(\d+)/);
    if (!wcMatch || !wcMatch[1]) {
      throw new Error('No WooCommerce product ID found in URL');
    }

    const productId = wcMatch[1];
    const urlObj = new URL(cartUrl);
    const ajaxEndpoint = `${urlObj.origin}/?wc-ajax=add_to_cart`;

    const formData = new FormData();
    formData.append('product_id', productId);
    formData.append('quantity', String(currentQty));

    const response = await fetch(ajaxEndpoint, {
      method: 'POST',
      body: formData,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`WC AJAX returned ${response.status}`);
    }

    let data;
    try {
      data = await response.json();
    } catch (e) {
      throw new Error('WC AJAX response was not valid JSON');
    }

    if (data && data.error) {
      throw new Error(typeof data.error === 'string' ? data.error : 'WooCommerce returned an error');
    }

    // Trigger WC fragment refresh only if jQuery is loaded
    if (window.jQuery && typeof window.jQuery.fn.trigger === 'function') {
      try {
        window.jQuery(document.body).trigger('wc_fragment_refresh');
        window.jQuery(document.body).trigger('added_to_cart', [data?.fragments, data?.cart_hash]);
      } catch (e) {
        this.log('WC fragment refresh failed (non-critical):', e.message);
      }
    }

    return data;
  }

  // --- Cart v2: Plugin-first add-to-cart for WordPress/WooCommerce ---

  _isCartV2Available() {
    const cart = this.config.cart;
    return !!(cart && cart.cartV2Enabled && cart.provider === 'woocommerce' && cart.endpoint);
  }

  /**
   * Normalize attributes from backend format to flat object.
   * Backend may send: [{attribute: "pa_size", value: "L"}] or {pa_size: "L"} or null
   * Plugin expects: {pa_size: "L"}
   */
  _normalizeAttributes(attrs) {
    if (!attrs) return {};
    if (Array.isArray(attrs)) {
      const obj = {};
      for (const item of attrs) {
        if (item && item.attribute && item.value !== undefined) {
          obj[item.attribute] = String(item.value);
        }
      }
      return obj;
    }
    if (typeof attrs === 'object') return { ...attrs };
    return {};
  }

  async _handleCartV2(normalized, currentQty) {
    const cart = this.config.cart;
    const meta = normalized.cartPayload || {};
    const cartBlock = normalized.cart || {};

    // Merge attributes from both sources (cartBlock has priority over payload)
    const attrsFromPayload = this._normalizeAttributes(meta.attributes);
    const attrsFromCart = this._normalizeAttributes(cartBlock.attributes);
    const attributes = { ...attrsFromPayload, ...attrsFromCart };

    const payload = {
      product_id: Number(normalized.wooProductId) || Number(normalized.id) || 0,
      quantity: currentQty,
      variation_id: Number(cartBlock.variation_id || meta.variation_id || 0),
      parent_product_id: Number(normalized.parentProductId || cartBlock.parent_product_id || meta.product_id || 0),
      attributes: attributes,
      product_type: normalized.productType || '',
      product_url: normalized.productUrl || '',
      _miptech_nonce: cart.nonce,  // Nonce in body as fallback (some servers strip custom headers)
    };

    this.log('Cart v2 request:', payload);

    const response = await fetch(cart.endpoint, {
      method: 'POST',
      headers: (() => {
        const h = {
          'Content-Type': 'application/json',
          'X-MipTech-Cart-Nonce': cart.nonce,
        };
        if (cart.wpNonce) h['X-WP-Nonce'] = cart.wpNonce;
        return h;
      })(),
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Cart v2 endpoint returned ${response.status}`);
    }

    const result = await response.json();
    this.log('Cart v2 response:', result);
    return result;
  }

  _triggerCartEvents(result) {
    try {
      document.dispatchEvent(new CustomEvent('miptech:cart-added', { detail: result }));
    } catch (e) { /* ignore */ }

    if (window.jQuery && typeof window.jQuery.fn.trigger === 'function') {
      try {
        window.jQuery(document.body).trigger('wc_fragment_refresh');
        window.jQuery(document.body).trigger('added_to_cart', [null, null]);
      } catch (e) {
        this.log('WC fragment refresh failed (non-critical):', e.message);
      }
    }
  }

  // Updated Render Method for Guided Selling V2
  renderProductRecommendations(products, mode = 'options', heroId = null) {
    if (!products || products.length === 0) return;

    // Dedupe: hash product IDs to avoid rendering same set twice
    const productKey = 'products:' + products.map(p => p.id || p.product_id || '').sort().join(',');
    if (this._renderedMessageIds.has(productKey)) {
      this.log('Skipping duplicate product recommendations');
      return;
    }
    this._renderedMessageIds.add(productKey);

    // Persist to chatHistory (skip during restore)
    if (!this._restoring) {
      this.chatHistory.push({
        type: 'products',
        data: {
          products: products.map(p => {
            const normalized = this._normalizeProduct(p);
            const meta = p.metadata || {};
            return {
              id: normalized.id,
              product_id: p.product_id || p.id || null,
              name: normalized.name || p.name,
              price: p.price,
              url_prodotto: normalized.productUrl || null,
              product_type: normalized.productType || null,
              cta_type: normalized.ctaType || null,
              direct_purchasable: normalized.directPurchasable,
              add_to_cart_url: normalized.rawCartUrl || null,
              add_to_cart_payload: normalized.cartPayload || null,
              cart: normalized.cart || null,
              image_url: p.image_url || p.img_url || meta.image_url || null,
              brand: p.brand || meta.brand || null
            };
          }),
          mode,
          heroId
        },
        ts: Date.now()
      });
      this._persistChat();
    }

    // Limit to 3 for V2 modes, fallback to 5 for legacy
    // V2 modes: 'hero', 'options', 'clarify'
    const limit = (mode === 'hero' || mode === 'options' || mode === 'clarify') ? 3 : 5;
    const productsToRender = products.slice(0, limit);

    const messagesContainer = this.chatWindow.querySelector('.miptech-chat-messages');

    // Create a Wrapper for relative positioning (arrows)
    const wrapper = document.createElement('div');
    wrapper.className = 'miptech-carousel-wrapper';

    // Label for Clarify Mode
    if (mode === 'clarify') {
      const label = document.createElement('div');
      Object.assign(label.style, {
        fontSize: '12px',
        color: '#666',
        marginBottom: '6px',
        paddingLeft: '4px',
        fontWeight: '600'
      });
      label.textContent = 'Scelte popolari';
      messagesContainer.appendChild(label);
    }

    const recsContainer = document.createElement('div');
    recsContainer.className = 'miptech-product-recs';

    productsToRender.forEach(product => {
      // Legacy Score Filter (V1 only)
      if (!mode && typeof product.score === 'number' && product.score < 0.50) {
        this.log(`Skipping low score product (${product.score}): ${product.name}`);
        return;
      }

      const card = document.createElement('a');
      card.className = 'miptech-product-card';
      // Robust URL extraction with multiple fallbacks
      const productUrl = product.url_prodotto || product.url || product.link || product.product_url ||
        (product.metadata ? (product.metadata.url_prodotto || product.metadata.url || product.metadata.link) : null) ||
        null;
      if (productUrl) {
        card.href = productUrl;
        // Flush state before same-tab navigation
        card.addEventListener('click', () => {
          this._persistChat();
        });
      } else {
        card.removeAttribute('href');
        card.style.cursor = 'default';
      }

      // === HERO MODE STYLING ===
      if (mode === 'hero' && String(product.id) === String(heroId)) {
        card.classList.add('is-hero');

        // Badge
        const badge = document.createElement('div');
        badge.className = 'miptech-hero-badge';
        badge.textContent = "Consigliato dall'AI";
        Object.assign(badge.style, {
          position: 'absolute',
          top: '0',
          right: '0',
          background: this.config.brandColor,
          color: 'white',
          fontSize: '10px',
          fontWeight: '700',
          padding: '4px 8px',
          borderRadius: '0 0 0 8px',
          zIndex: '5'
        });
        card.appendChild(badge);
      }

      // Image Handling
      const image = document.createElement('img');
      image.className = 'miptech-product-image';

      let imgUrl = product.image_url || product.img_url ||
        (product.metadata ? product.metadata.image_url : null) ||
        'https://via.placeholder.com/150?text=No+Image';

      if (imgUrl && imgUrl.includes('storeden.com') && imgUrl.includes('404')) {
        imgUrl = 'https://via.placeholder.com/150?text=N/A';
      }

      image.src = imgUrl;
      image.alt = product.name || 'Product';
      image.referrerPolicy = "no-referrer";
      image.loading = "lazy";
      image.onerror = () => { image.src = 'https://via.placeholder.com/150?text=N/A'; };

      card.appendChild(image);

      const info = document.createElement('div');
      info.className = 'miptech-product-info';

      const name = document.createElement('h4');
      name.className = 'miptech-product-name';
      name.textContent = product.name || 'Prodotto';
      info.appendChild(name);

      if (product.prezzo_unitario || product.price) {
        const price = document.createElement('div');
        price.className = 'miptech-product-price';
        const pVal = product.prezzo_unitario || product.price;
        price.textContent = typeof pVal === 'number' ? `€${pVal.toFixed(2)}` : pVal;
        info.appendChild(price);
      }

      // Actions Container (vertical layout)
      const actions = document.createElement('div');
      actions.className = 'miptech-product-actions';
      Object.assign(actions.style, {
        display: 'flex',
        flexDirection: 'column',
        marginTop: '8px',
        gap: '8px'
      });

      // --- CTA Resolution (contract-driven) ---
      const normalized = this._normalizeProduct(product);
      let cta = this._resolveCTA(normalized);

      // Guard: se CTA non-cart ma manca productUrl, fallback
      if (cta && cta.kind !== 'add_to_cart' && !cta.href) {
        this.log('⚠️ CTA skipped: missing productUrl', {
          product: normalized.name,
          ctaKind: cta.kind
        });
        cta = null;
      }

      // Logging
      this.log('Product CTA resolved', {
        tenant: this.config.tenantId,
        product: normalized.name,
        productType: normalized.productType,
        ctaKind: cta ? cta.kind : 'legacy',
        directPurchasable: normalized.directPurchasable,
        cartMode: normalized.cart?.mode || null
      });

      if (this._usesProductRedirectSafeMode() && cta) {
        this.log('Product redirect safe mode: redirect to product page', {
          product: normalized.name,
          reason: 'remote_cart_unreliable'
        });
      }

      if (cta && cta.kind !== 'add_to_cart') {
        // --- Contract CTA: single button replacing "Scopri di più" + cart ---
        const ctaBtn = document.createElement('div');
        ctaBtn.className = 'miptech-product-cta';
        ctaBtn.textContent = cta.label;
        Object.assign(ctaBtn.style, {
          width: '100%',
          textAlign: 'center',
          cursor: 'pointer'
        });
        ctaBtn.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._persistChat();
          window.location.assign(cta.href);
        };
        actions.appendChild(ctaBtn);

      } else {
        // --- Legacy path (cta === null) or contract add_to_cart ---

        // 1. Details Button ("Scopri di più") - full width
        const detailsBtn = document.createElement('div');
        detailsBtn.className = 'miptech-product-cta';
        detailsBtn.textContent = 'SCOPRI DI PIÙ';
        Object.assign(detailsBtn.style, {
          width: '100%',
          textAlign: 'center'
        });
        actions.appendChild(detailsBtn);

        // 2. Add to Cart Button — legacy or contract add_to_cart
        let cartUrl = null;
        let cartPayload = null;

        if (cta && cta.kind === 'add_to_cart' && this._isCartV2Available()) {
          // Cart v2: server-side path. No cartUrl needed for primary flow.
          cartUrl = '__cart_v2__';
          cartPayload = normalized.cartPayload;
        } else if (cta && cta.kind === 'add_to_cart') {
          // Legacy contract: use resolved href
          cartUrl = cta.href;
          cartPayload = normalized.cartPayload;
        } else {
          // Legacy CTA decision when the backend does not provide an explicit contract.
          this.log('CTA fallback: legacy path', {
            tenant: this.config.tenantId,
            product: normalized.name
          });

          const rawCartUrl = normalized.rawCartUrl;
          const productType = normalized.productType;
          const ctaType = normalized.ctaType;
          cartPayload = normalized.cartPayload;
          const productId = normalized.id;

          const NON_DIRECT_TYPES = ['bundle', 'variable', 'grouped', 'composite', 'subscription_variation'];
          const isViewOptions = ctaType === 'view_options';
          const isNonDirectType = productType && NON_DIRECT_TYPES.includes(productType.toLowerCase());

          if (isViewOptions && !cartPayload) {
            this.log('ℹ️ Product requires options, skipping cart button:', normalized.name,
              { type: productType, cta_type: ctaType });
          } else if (cartPayload) {
            if (rawCartUrl && /[?&]add-to-cart=\d+/.test(rawCartUrl)) {
              cartUrl = rawCartUrl;
            }
          } else if (rawCartUrl && /[?&]add-to-cart=\d+/.test(rawCartUrl)) {
            cartUrl = rawCartUrl;
          } else if (productId && !isNonDirectType) {
            let storeOrigin = normalized.storeBaseUrl || '';
            if (!storeOrigin) storeOrigin = window.location.origin;
            cartUrl = `${storeOrigin}/?add-to-cart=${productId}`;
          } else if (isNonDirectType && !cartPayload) {
            this.log('ℹ️ Non-direct product without payload, skipping cart button:', normalized.name,
              { type: productType });
          }
        }

        if (cartUrl) {
          this.log('✅ Add to Cart URL (validated):', cartUrl);

          // Quantity Selector
          let currentQty = 1;

          const qtySelector = document.createElement('div');
          qtySelector.className = 'miptech-qty-selector';

          const minusBtn = document.createElement('button');
          minusBtn.className = 'miptech-qty-btn';
          minusBtn.textContent = '\u2212'; // Unicode minus
          minusBtn.type = 'button';

          const qtyValue = document.createElement('span');
          qtyValue.className = 'miptech-qty-value';
          qtyValue.textContent = '1';

          const plusBtn = document.createElement('button');
          plusBtn.className = 'miptech-qty-btn';
          plusBtn.textContent = '+';
          plusBtn.type = 'button';

          minusBtn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (currentQty > 1) {
              currentQty--;
              qtyValue.textContent = currentQty;
            }
          };

          plusBtn.onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (currentQty < 99) {
              currentQty++;
              qtyValue.textContent = currentQty;
            }
          };

          qtySelector.appendChild(minusBtn);
          qtySelector.appendChild(qtyValue);
          qtySelector.appendChild(plusBtn);

          // Cart row: qty selector + cart button on same line
          const cartRow = document.createElement('div');
          Object.assign(cartRow.style, {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            gap: '8px'
          });

          cartRow.appendChild(qtySelector);

          const cartBtn = document.createElement('a');
          cartBtn.className = 'miptech-cart-btn';
          cartBtn.href = cartUrl;

          // Icon SVG
          const cartIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path></svg>`;
          const spinnerIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="animation: spin 1s linear infinite;"><path d="M21 12a9 9 0 1 1-6.219-8.56"></path></svg>`;
          const checkIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>`;

          // Inject Spinner Keyframes if not present
          if (!document.getElementById('miptech-spin-style')) {
            const style = document.createElement('style');
            style.id = 'miptech-spin-style';
            style.textContent = `@keyframes spin { 100% { transform: rotate(360deg); } }`;
            document.head.appendChild(style);
          }

          cartBtn.innerHTML = cartIcon;

          // Add to Cart Handler — 2-mode system (redirect / AJAX)
          const externalLinkIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>`;

          cartBtn.onclick = async (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (cartBtn.dataset.state === 'success' || cartBtn.dataset.state === 'redirected') return;

            // --- Cart v2: Plugin-first path for WordPress/WooCommerce ---
            if (this._isCartV2Available()) {
              this.log('Cart v2: using plugin endpoint', { product: normalized.name, qty: currentQty });

              try {
                cartBtn.innerHTML = spinnerIcon;
                const result = await this._handleCartV2(normalized, currentQty);

                if (result.action === 'added' && result.success) {
                  cartBtn.innerHTML = checkIcon;
                  cartBtn.style.backgroundColor = '#4CAF50';
                  cartBtn.title = currentQty > 1 ? `${currentQty} aggiunti al carrello!` : 'Aggiunto al carrello!';
                  cartBtn.dataset.state = 'success';
                  this._triggerCartEvents(result);
                  setTimeout(() => {
                    cartBtn.innerHTML = cartIcon;
                    cartBtn.style.backgroundColor = this.config.brandColor;
                    cartBtn.title = '';
                    delete cartBtn.dataset.state;
                  }, 3000);
                  return;
                }

                if (result.action === 'redirect' || result.action === 'collect_data') {
                  const redirectUrl = result.redirect_url || normalized.productUrl;
                  if (redirectUrl) {
                    this.log('Cart v2: server says ' + result.action + ', redirecting to', redirectUrl);
                    this._persistChat();
                    window.location.assign(redirectUrl);
                    return;
                  }
                  // No redirect URL available — show error state, do NOT fall through to legacy
                  this.log('Cart v2: ' + result.action + ' but no redirect URL');
                  cartBtn.innerHTML = cartIcon;
                  return;
                }

                if (result.action === 'error') {
                  // Business error from server (out_of_stock, not_purchasable, etc.)
                  // Do NOT fall through to legacy — the server is the source of truth.
                  this.log('Cart v2: business error:', result.error?.code, result.error?.message);

                  // If server provides a redirect URL (e.g. product page), use it
                  if (result.redirect_url) {
                    this._persistChat();
                    window.location.assign(result.redirect_url);
                    return;
                  }

                  // Otherwise reset button and stop — no legacy fallback for business errors
                  cartBtn.innerHTML = cartIcon;
                  return;
                }

                // Unexpected response shape — treat as transport error, allow legacy fallback
                this.log('Cart v2: unexpected response shape, falling back to legacy', result);
                cartBtn.innerHTML = cartIcon;

              } catch (err) {
                // Transport/network error only — legacy fallback is appropriate here
                this.log('Cart v2 transport error, falling back to legacy:', err.message);
                cartBtn.innerHTML = cartIcon;
                // Fall through to legacy path below
              }
            }

            // --- Legacy path (non-WP platforms, cart v2 disabled, or v2 failure fallback) ---
            if (cartUrl === '__cart_v2__') {
              // Cart v2 was the only path but failed — redirect to product page as last resort
              if (normalized.productUrl) {
                this._persistChat();
                window.location.assign(normalized.productUrl);
              }
              return;
            }

            const mode = this._resolveCartMode(cartUrl);
            this.log('Cart legacy: mode=' + mode, 'qty=' + currentQty, 'url=' + cartUrl);

            if (mode === 'redirect' && /[?&]add-to-cart=\d+/.test(cartUrl)) {
              const url = new URL(cartUrl, window.location.href);
              if (currentQty > 1) url.searchParams.set('quantity', String(currentQty));
              this._persistChat();
              window.location.assign(url.toString());
              return;
            }

            if (mode === 'redirect') {
              this._persistChat();
              this._handleCartRedirect(cartUrl, currentQty, cartPayload, false);
              cartBtn.innerHTML = externalLinkIcon;
              cartBtn.title = 'Aperto in nuova scheda';
              cartBtn.dataset.state = 'redirected';
              setTimeout(() => {
                cartBtn.innerHTML = cartIcon;
                cartBtn.style.backgroundColor = this.config.brandColor;
                cartBtn.title = '';
                delete cartBtn.dataset.state;
              }, 2500);
              return;
            }

            // Legacy AJAX mode
            try {
              cartBtn.innerHTML = spinnerIcon;
              const data = await this._handleCartAjax(cartUrl, currentQty);
              cartBtn.innerHTML = checkIcon;
              cartBtn.style.backgroundColor = '#4CAF50';
              cartBtn.title = currentQty > 1 ? `${currentQty} aggiunti al carrello!` : 'Aggiunto al carrello!';
              cartBtn.dataset.state = 'success';
              setTimeout(() => {
                cartBtn.innerHTML = cartIcon;
                cartBtn.style.backgroundColor = this.config.brandColor;
                cartBtn.title = '';
                delete cartBtn.dataset.state;
              }, 3000);
            } catch (err) {
              this.log('Legacy AJAX failed, degrading to redirect:', err.message);
              this._handleCartRedirect(cartUrl, currentQty, cartPayload, false);
              this._cartCapability = 'redirect';
            }
          };

          Object.assign(cartBtn.style, {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '32px',
            height: '32px',
            backgroundColor: this.config.brandColor,
            borderRadius: '50%',
            color: 'white',
            textDecoration: 'none',
            flexShrink: '0',
            cursor: 'pointer',
            transition: 'transform 0.2s ease, background-color 0.2s ease'
          });

          // Hover effect
          cartBtn.onmouseenter = () => { if (cartBtn.innerHTML !== checkIcon) cartBtn.style.transform = 'scale(1.1)'; };
          cartBtn.onmouseleave = () => { cartBtn.style.transform = 'scale(1)'; };

          cartRow.appendChild(cartBtn);
          actions.appendChild(cartRow);
        } else {
          this.log('ℹ️ No cart button for product:', normalized.name,
            '(type:', normalized.productType, ', cta:', normalized.ctaType, ')');
        }
      }

      info.appendChild(actions);

      card.appendChild(info);
      recsContainer.appendChild(card);
    });

    wrapper.appendChild(recsContainer);
    messagesContainer.appendChild(wrapper);

    // Inject Navigation Arrows and Initialize Carousel
    if (productsToRender.length > 1) {
      this.injectCarouselArrows(wrapper, recsContainer);
      // Apply transforms IMMEDIATELY to prevent flash of unstyled content
      this.initCarousel(wrapper, recsContainer);
    } else if (productsToRender.length === 1) {
      // Single product: activate it directly (no carousel needed)
      const singleCard = recsContainer.querySelector('.miptech-product-card');
      if (singleCard) {
        singleCard.classList.add('miptech-card-active');
      }
    }

    if (this.scrollToBottom) {
      this.scrollToBottom();
    }
  }

  injectCarouselArrows(wrapper, container) {
    const createArrow = (direction) => {
      const btn = document.createElement('button');
      btn.className = `miptech-carousel-arrow ${direction}`;
      btn.innerHTML = direction === 'prev'
        ? '<svg viewBox="0 0 24 24"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>'
        : '<svg viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>';

      btn.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const cardWidth = container.querySelector('.miptech-product-card')?.offsetWidth || 300;
        const containerWidth = container.clientWidth;
        const cardMargin = containerWidth * 0.04; // 4% of container per side
        const scrollAmount = direction === 'next'
          ? cardWidth + cardMargin * 2
          : -(cardWidth + cardMargin * 2);

        container.scrollBy({ left: scrollAmount, behavior: 'smooth' });

        // Update transforms after scroll animation completes
        setTimeout(() => {
          this.updateCarouselTransforms(container);
        }, 350); // Match CSS transition duration (0.35s)
      };
      return btn;
    };

    const prevBtn = createArrow('prev');
    const nextBtn = createArrow('next');
    prevBtn.style.display = 'none';

    wrapper.appendChild(prevBtn);
    wrapper.appendChild(nextBtn);

    const handleScroll = () => {
      const tolerance = 5;
      prevBtn.style.display = (container.scrollLeft > tolerance) ? 'flex' : 'none';
      nextBtn.style.display = (container.scrollLeft + container.clientWidth >= container.scrollWidth - tolerance) ? 'none' : 'flex';
    };

    container.addEventListener('scroll', handleScroll);
  }

  updateCarouselTransforms(container) {
    const cards = Array.from(container.querySelectorAll('.miptech-product-card'));

    if (cards.length === 0) return;

    // Use getBoundingClientRect for accurate viewport-relative positioning
    const containerRect = container.getBoundingClientRect();
    const containerCenter = containerRect.left + containerRect.width / 2;

    // Find card closest to container center
    let closestIndex = 0;
    let closestDistance = Infinity;

    cards.forEach((card, index) => {
      const cardRect = card.getBoundingClientRect();
      const cardCenter = cardRect.left + cardRect.width / 2;
      const distance = Math.abs(cardCenter - containerCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    // Apply active class and transforms to cards
    cards.forEach((card, index) => {
      if (index === closestIndex) {
        // Center card: attiva, nessun offset
        card.classList.add('miptech-card-active');
        card.style.transform = 'scale(1) translateX(0)';
      } else {
        // Side cards: simmetrico su entrambi i lati, NO offset
        card.classList.remove('miptech-card-active');
        card.style.transform = 'scale(0.75) translateX(0)';
      }
    });
  }

  initCarousel(wrapper, container) {
    // Force layout calculation before applying transforms
    void container.offsetWidth;

    // Initial transform application
    this.updateCarouselTransforms(container);

    // Optimized scroll handler with requestAnimationFrame
    let scrollTicking = false;

    const handleScroll = () => {
      if (!scrollTicking) {
        requestAnimationFrame(() => {
          this.updateCarouselTransforms(container);
          scrollTicking = false;
        });
        scrollTicking = true;
      }
    };

    // Attach scroll listener
    container.addEventListener('scroll', handleScroll, { passive: true });

    // --- Drag-to-scroll (mouse + touch) ---
    let isDragging = false;
    let startX = 0;
    let scrollStart = 0;

    const onDragStart = (e) => {
      // Only left mouse button or touch
      if (e.type === 'mousedown' && e.button !== 0) return;

      isDragging = true;
      startX = e.type === 'mousedown' ? e.pageX : e.touches[0].pageX;
      scrollStart = container.scrollLeft;
      container.classList.add('miptech-dragging');
    };

    const onDragMove = (e) => {
      if (!isDragging) return;

      const currentX = e.type === 'mousemove' ? e.pageX : e.touches[0].pageX;
      const delta = startX - currentX;
      container.scrollLeft = scrollStart + delta;
    };

    const onDragEnd = () => {
      if (!isDragging) return;
      isDragging = false;
      container.classList.remove('miptech-dragging');

      // Re-enable snap: scroll-snap will auto-snap to nearest card
      // Update transforms after snap settles
      setTimeout(() => {
        this.updateCarouselTransforms(container);
      }, 350);
    };

    // Mouse events
    container.addEventListener('mousedown', onDragStart);
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);

    // Touch events
    container.addEventListener('touchstart', onDragStart, { passive: true });
    container.addEventListener('touchmove', onDragMove, { passive: true });
    container.addEventListener('touchend', onDragEnd);

    // Prevent link clicks after drag
    container.addEventListener('click', (e) => {
      if (Math.abs(startX - (e.pageX || 0)) > 5) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    // Store reference for cleanup
    if (!this.carouselCleanupHandlers) {
      this.carouselCleanupHandlers = [];
    }
    this.carouselCleanupHandlers.push({
      container,
      handler: handleScroll
    });
  }

  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : null;
  }


  isAllowedUrl(url) {
    if (!url) return false;
    try {
      const parsed = new URL(url);

      // Basic protocol check
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        return false;
      }

      const productHosts = this.config.allowedProductHosts;
      const imageHosts = this.config.allowedImageHosts;

      // If no whitelists are configured, allow all valid HTTP/HTTPS URLs (Permissive Mode)
      if ((!productHosts || !Array.isArray(productHosts) || productHosts.length === 0) &&
        (!imageHosts || !Array.isArray(imageHosts) || imageHosts.length === 0)) {
        return true;
      }

      // If whitelists exist, enforce them (Strict Mode)
      const isAllowedProduct = productHosts && productHosts.includes(parsed.hostname);
      const isAllowedImage = imageHosts && imageHosts.includes(parsed.hostname);

      return isAllowedProduct || isAllowedImage;
    } catch (e) {
      return false;
    }
  }

  safeUrl(url) {
    if (this.isAllowedUrl(url)) {
      return url;
    }
    return '#';
  }
}

// Auto-initialize if config is present
if (typeof window !== 'undefined' && window.MIPTechWidgetConfig) {
  document.addEventListener('DOMContentLoaded', () => {
    new MIPTechWidget(window.MIPTechWidgetConfig);
  });
}

window.MIPTechWidget = MIPTechWidget;
